import React, { useState, useEffect, useRef } from "react";
import EventService from "../../../../../service/event/EventService";
import NavigoAPIServices from "../services/navigoAPIServices";
import PatientsService from "../../../../ops/modules/patients/service/PatientsService";
import ThemedDropdown from "./ThemedDropdown";
import WalletDetailsDialog from "./WalletDetailsDialog";
import BankAccountVerification from "./helper/bank-account-verification";
import ChangeAccountForClaimMiddleware from "./helper/change-account-for-claim-middleware";
import BankAccountSuccessScreen from "./helper/bank-account-success-screen";
import BankAccountService from "../../../../ops/modules/patients/modules/new-bridge/services/BankAccountService";
import AuthenticationService from "../../../../../service/auth/AuthenticationService";
import { Modal } from "@material-ui/core";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import uploadIcon from "../../../../../assets/icons/upload_icon.png";
import "../styles/claimDetail.scss";

const api = new NavigoAPIServices();
const patientService = new PatientsService();
const bankAccountService = new BankAccountService();
const authenticationService = new AuthenticationService();

// The "Sublimit exception" feature is temporarily disabled — its checkbox in the
// claim-type-change dialog is hidden and the flag is never sent as true. Flip to
// true to bring it back.
const SHOW_SUBLIMIT_EXCEPTION = false;

// Prescription capture (hospital identity/address, chief complaint, diagnosis) is
// extracted and stored for insurers that ask for it, but the card is not shown to
// ops yet. Flip to true to reveal it. The data is captured either way — hiding the
// card does not stop the workflow writing it.
const SHOW_PRESCRIPTION_DETAILS = true;

// Status remarks are authored in a rich-text editor, so an "empty" remark is
// usually HTML like "<p></p>" / "<p><br></p>" rather than "". Strip tags/entities
// to decide whether there is any real text. Same helper as ClaimStatusCell.
// The member guidance is generated as PLAIN TEXT on purpose (the editor's own
// markup carries inline styles, including a malformed `font-size: 10ptpx` that
// is all over the existing stored comments). Wrap it in minimal paragraphs so
// the wallet stores and the status history renders it the same as a typed one.
const plainToHtml = (text) =>
  String(text || "")
    .split(/\n{2,}/)
    .map(
      (block) =>
        "<p>" +
        block
          .split("\n")
          .map((line) =>
            line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"),
          )
          .join("<br/>") +
        "</p>",
    )
    .join("");

const remarkHasText = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim().length > 0;

// Toolbar kept to what a claim remark actually needs — the full default toolbar
// (colours, embeds, images) overflows the dialog and invites markup the status
// history can't render usefully.
const REMARKS_TOOLBAR = {
  options: ["inline", "list", "link", "history"],
  inline: { options: ["bold", "italic", "underline", "strikethrough"] },
  list: { options: ["unordered", "ordered"] },
};

const DOC_ACCEPT_TYPES = "application/pdf,image/png,image/jpeg,image/jpg";
const extOf = (name) => (name || "").split(".").pop().toUpperCase();
const getDocumentFormat = (type) => {
  switch ((type || "").toLowerCase()) {
    case "jpg":
    case "jpeg":
      return 1;
    case "png":
      return 2;
    case "gif":
      return 3;
    case "pdf":
      return 4;
    default:
      return 0;
  }
};

const maskedBankNum = (last4, accNumLength) =>
  "X".repeat(Math.abs((accNumLength || 4) - 4)) +
  (last4 == null ? "" : last4).toString();
const BANK_STATUS_LABEL = { 0: "Unverified", 1: "Verified", 2: "Rejected" };
const BANK_STATUS_CLASS = { 0: "unverified", 1: "verified", 2: "rejected" };
const isZeroObjectId = (id) =>
  !id || id === "000000000000000000000000" || id === "0";

const rupees = (v) =>
  v === null || v === undefined ? "—" : "₹" + Number(v).toLocaleString("en-IN");

const DECISION_LABEL = {
  APPROVED: "Approved",
  REJECTED: "Rejected",
  ON_HOLD: "On hold",
  SKIPPED: "Skipped",
};
const DECISION_BADGE = {
  APPROVED: "approved",
  REJECTED: "rejected",
  ON_HOLD: "on_hold",
  SKIPPED: "skipped",
};
// The re-filing rule. Its FAIL verdict carries matchedClaimIds — the earlier
// claim(s) the workflow matched — which the screen links to.
const DUPLICATE_RULE_ID = "CLAIM_NOT_DUPLICATE";
// Wallet claim lifecycle (the header status the evaluator can change).
const WALLET_STATUS = {
  1: "Received",
  2: "Approved",
  3: "Rejected",
  4: "Processed",
  5: "Appealed",
  6: "On Hold",
  7: "Provisionally Approved",
  8: "Resubmitted",
  9: "Draft",
};
const WALLET_STATUS_PILL = {
  1: "pending",
  2: "approved",
  3: "rejected",
  4: "approved",
  5: "pending",
  6: "on-hold",
  7: "approved",
  8: "pending",
  9: "draft",
};

// A decided claim — approved, rejected or paid out. The AI evaluation is frozen
// from here on (the backend enforces the same rule): it records whether the AI
// was right about the claim AS DECIDED, so a later edit would restate a settled
// claim. Appealing a rejection moves the claim back to Appealed, which reopens
// the evaluation along with the claim.
const DECIDED_STATUSES = [2, 3, 4];
const isDecidedStatus = (status) =>
  DECIDED_STATUSES.indexOf(Number(status)) !== -1;

// Status-transition rules — an EXACT replica of the old claim dashboard's
// per-status disabled logic (ClaimStatusCell's MenuItem `disabled` conditions).
// For each target status, this returns whether it's selectable from the claim's
// current status. Deliberately STRICTER than the wallet's own validator so the
// two dashboards behave identically:
//   Received(1)       never selectable (initial state only)
//   Approved(2)       from Received / Appealed / On Hold / Provisional / Resubmitted
//   Rejected(3)       from Received / Appealed / On Hold / Provisional / Resubmitted
//   Processed(4)      from Approved only
//   Appealed(5)       from Rejected only
//   On Hold(6)        from Received / Appealed / On Hold / Provisional / Resubmitted
//   Prv. Approved(7)  from Received / Appealed / On Hold / Resubmitted
//   Resubmitted(8)    from On Hold only
const canMoveStatus = (newStatus, prevStatus) => {
  const cur = Number(prevStatus);
  switch (Number(newStatus)) {
    case 2: // Approved
    case 3: // Rejected
    case 6: // On Hold
      return [1, 5, 6, 7, 8].indexOf(cur) !== -1;
    case 4: // Processed
      return cur === 2;
    case 5: // Appealed
      return cur === 3;
    case 7: // Prv. Approved
      return [1, 5, 6, 8].indexOf(cur) !== -1;
    case 8: // Resubmitted
      return cur === 6;
    default: // Received (1) and anything else is never a valid target
      return false;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

// The evaluator is the logged-in Bridge user — read from the auth profile's
// employeeProfile (firstName/lastName), the SAME source the dashboard uses. The
// old localStorage lookup returned "" (those fields don't exist there), so Save
// sent evaluatedBy="" and the backend overwrote the claim's evaluator with an
// empty string — reverting the dashboard row from the name back to "Evaluate".
const getEvaluator = () => {
  try {
    const emp = authenticationService.getUserProfile()?.employeeProfile;
    return emp
      ? [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim()
      : "";
  } catch (e) {
    return "";
  }
};

// Canonical signature of the evaluator layer (verdicts + comments + the
// Correct/Incorrect answer). Compared against the baseline captured at load /
// after a save to tell whether the form carries UNSAVED edits — so toggling a
// mark and toggling it back reads as clean, not dirty.
const packVerdicts = (verdicts, comments) =>
  Array.from(
    new Set(Object.keys(verdicts || {}).concat(Object.keys(comments || {}))),
  )
    .sort()
    .map((k) => {
      const v = (verdicts || {})[k] == null ? "" : String((verdicts || {})[k]);
      const n = ((comments || {})[k] || "").trim();
      // A target that was touched and then emptied is the same as never touched.
      return v || n ? k + "|" + v + "|" + n : "";
    })
    .filter(Boolean)
    .join(";");

const reviewSignature = (s) =>
  JSON.stringify([
    s.isCorrect == null ? "" : String(s.isCorrect),
    (s.reason || "").trim(),
    packVerdicts(s.ruleVerdicts, s.ruleComments),
    packVerdicts(s.lineVerdicts, s.lineComments),
  ]);

const EMPTY_REVIEW_SIG = reviewSignature({});

// Readable text out of a claim-service error. Echo nests the handler's payload
// under `message`, so the body is { message: { code, message, errorMessage } } —
// `err.message` is an OBJECT there, and rendering it directly would throw.
const apiErrorText = (err, fallback) => {
  const m = err && err.message;
  if (typeof m === "string" && m) return m;
  if (m && typeof m.message === "string" && m.message) return m.message;
  return fallback;
};

// Whether the form differs from the last loaded/saved baseline. A function
// rather than an inline `!readOnly && … !== …` const because a top-level
// logical-expression const sitting between two hooks trips react-hooks'
// code-path analysis (see computeAiProcessing below for the same workaround).
const computeDirty = (readOnly, state, baseline) => {
  if (readOnly) return false;
  return reviewSignature(state) !== baseline;
};

// The evaluator layer is read-only in view mode (?view=1) AND once the claim has
// been decided. Same function-not-expression reason as computeDirty above.
const computeEvalReadOnly = (readOnly, walletStatus) => {
  if (readOnly) return true;
  return isDecidedStatus(walletStatus);
};

const BackArrow = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <path
      d="M12 15.25L6.75 10L12 4.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.25 10H6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Clock = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="0.9" />
    <path
      d="M6 3.75V6L7.2 6.7"
      stroke="currentColor"
      strokeWidth="0.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WalletGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path
      d="M3 7.5A2.5 2.5 0 0 1 5.5 5H17a2 2 0 0 1 2 2v.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 7.5v9A2.5 2.5 0 0 0 5.5 19H19a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2H5.5A2.5 2.5 0 0 1 3 7.5Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="16.5" cy="13" r="1.2" fill="currentColor" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 20h4l10.5-10.5a2 2 0 0 0 0-2.83l-1.17-1.17a2 2 0 0 0-2.83 0L4 16v4Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 6.5l4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const ChatGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 4H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h3v3.5L11.5 17H20a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1Z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
);

const TickIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
    <path
      d="M4.5 10.5L8 14L15.5 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon = () => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
    <path
      d="M6 6l8 8M14 6l-8 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// True only while the AI is still working and no decision has landed yet — a
// re-trigger flips status back to PROCESSING while keeping the prior assessment,
// which we still want to show (rather than a stale "processing" banner).
// A claim-type change is the exception: it drops the assessment server-side
// (the old verdict was computed for the old category), so the record comes back
// decision-less and this turns true on its own.
const computeAiProcessing = (rec) => {
  if (!rec) return false;
  const hasDecision = !!(rec.assessment && rec.assessment.decision);
  return (
    String(rec.status || "").toUpperCase() === "PROCESSING" && !hasDecision
  );
};

// Ids the claim-type options are keyed on. Read through helpers rather than a
// conditional const at component top level — see computeAiProcessing above for
// why a branch between two hooks upsets react-hooks' code-path analysis.
const planIdOf = (rec) => (rec ? rec.planId : 0);
const patientIdOf = (rec) => (rec ? rec.patientId : 0);

// The wallet only accepts a claim-type change while the claim is Received or On
// Hold (utils.IsValidStatusForReimbursementTypeChange). Mirrored here so the
// control is disabled up front instead of failing with a 412 on submit.
const canEditClaimType = (status) => [1, 6].indexOf(Number(status)) !== -1;

// How the claim was submitted, as stored on the wallet claim (0/absent = unset).
const DOCUMENT_TYPE_LABEL = { 1: "Softcopy", 2: "Hardcopy" };
const DOCUMENT_TYPE_OPTIONS = [
  { value: "1", label: "Softcopy" },
  { value: "2", label: "Hardcopy" },
];

// employeeUserId for the wallet's reimbursementTypeUpdate — it resolves the
// employee behind the change and rejects anything < 1.
const getEmployeeUserId = () => {
  const user = authenticationService.getUserProfile();
  return Number((user && user.id) || 0);
};

function ClaimAIReview(props) {
  const claimId =
    (props.match && props.match.params && props.match.params.claimId) || "";
  // Optional — passed from the dashboard row. Lets the backend return a wallet
  // "shell" (claim details only) when the claim has no AI record yet.
  const patientId =
    (props.match && props.match.params && props.match.params.patientId) || "";
  // View-only mode — opened from the dashboard's eye icon (?view=1). Everything
  // renders, but no control is editable.
  const readOnly =
    new URLSearchParams((props.location && props.location.search) || "").get(
      "view",
    ) === "1";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [record, setRecord] = useState(null);
  const [rules, setRules] = useState(null);
  const [accountName, setAccountName] = useState("");

  // PDF viewer
  const [docs, setDocs] = useState([]);
  const [docIndex, setDocIndex] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0); // image viewer rotation (deg)
  const [pdfPage, setPdfPage] = useState(1);
  const [docToken, setDocToken] = useState("");
  // Bridge user can attach a document on the fly from the viewer (no count limit).
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docUploadRef = useRef(null);

  // Evaluator
  const [isCorrect, setIsCorrect] = useState(null);
  const [reason, setReason] = useState("");
  const [ruleVerdicts, setRuleVerdicts] = useState({});
  const [lineVerdicts, setLineVerdicts] = useState({});
  // Per-target evaluator comments (stored as HumanVerdict.note) + the popup.
  const [ruleComments, setRuleComments] = useState({});
  const [lineComments, setLineComments] = useState({});
  const [commentTarget, setCommentTarget] = useState(null); // { kind, target, label }
  const [commentDraft, setCommentDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Signature of the review as last loaded from / written to the server. The
  // form is "dirty" whenever the current state differs from it — NOTHING in the
  // evaluator layer reaches the backend until Save is pressed.
  const savedSigRef = useRef(EMPTY_REVIEW_SIG);
  const dirtyRef = useRef(false);
  // Set once the evaluator has answered the unsaved-changes dialog and is on
  // their way out, so the replayed navigation isn't caught by the guard again.
  // A ref that survives re-renders, not a dirtyRef reset: a browser Back replay
  // (history.goBack) only lands on the next tick, by which time a render would
  // have put dirtyRef back to true and re-opened the dialog.
  const bypassGuardRef = useRef(false);
  // Navigation that was blocked because of unsaved edits: { location, action }.
  const [leaveTo, setLeaveTo] = useState(null);
  // Status id of a decision (Approve/Reject/Processed) held back because the
  // evaluation has unsaved edits — deciding would lock them out.
  const [pendingDecision, setPendingDecision] = useState(null);

  // Wallet claim status — edited in the header, updated separately from the AI
  // review (decision #4). Live-fetched by the backend into the assessment.
  const [walletStatus, setWalletStatus] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  // Bank account: masked details for the card item + the reused verify/change flow.
  const [bankResp, setBankResp] = useState(null);
  const [openBankVerify, setOpenBankVerify] = useState(false);
  const [openBankChange, setOpenBankChange] = useState(false);
  const [openBankSuccess, setOpenBankSuccess] = useState(false);
  const [bankSuccessHeading, setBankSuccessHeading] = useState("");
  const [bankSuccessDoneCb, setBankSuccessDoneCb] = useState(null);

  // Status-change dialog. Mirrors the old claims dashboard: picking a new status
  // opens a dialog that collects the status-specific data (approved amount /
  // reject reason / transaction id / on-hold remarks + re-upload flag) and a
  // confirmation, then applies it via updateClaim. `statusDialog` holds the
  // target status id being confirmed (number), or null when closed.
  const [statusDialog, setStatusDialog] = useState(null);
  const [savingDocType, setSavingDocType] = useState(false);
  // Which of the pencil-edited fields is currently open ("claimType" |
  // "documentType" | null). Only ever one at a time — opening one closes the
  // other, which is why this is a single value rather than a flag per field.
  const [editingField, setEditingField] = useState(null);
  const [dlgRemarks, setDlgRemarks] = useState(""); // HTML from the editor below
  const [dlgEditorState, setDlgEditorState] = useState(
    EditorState.createEmpty(),
  );
  const [dlgAmount, setDlgAmount] = useState("");
  const [dlgTxnId, setDlgTxnId] = useState("");
  const [dlgUploadAgain, setDlgUploadAgain] = useState(false);
  const [dlgConfirm, setDlgConfirm] = useState(false); // approve/on-hold second step
  const [dlgError, setDlgError] = useState(""); // server error shown inside the dialog
  const [currAvailAmt, setCurrAvailAmt] = useState(null); // approve: available claim amount
  const [fetchingAvail, setFetchingAvail] = useState(false);
  const [reasonList, setReasonList] = useState([]); // reject: predefined reasons
  const [fetchingReasons, setFetchingReasons] = useState(false);
  const [dlgReason, setDlgReason] = useState(""); // reject: selected reason
  const [guidanceCopied, setGuidanceCopied] = useState(false);

  // Claim-type change (the old dashboard's "Edit claim type", brought over).
  // Picking a new type opens a dialog that shows the sublimit available under it
  // and collects the mandatory reason; the wallet then re-adjudicates the claim
  // under the new category. `typeDialog` holds the target typeId, or null.
  const [claimTypes, setClaimTypes] = useState([]); // types offered by the plan
  const [typeDialog, setTypeDialog] = useState(null);
  const [typeReason, setTypeReason] = useState("");
  const [typeAvail, setTypeAvail] = useState(null); // available under the NEW type
  const [fetchingTypeAvail, setFetchingTypeAvail] = useState(false);
  const [typeException, setTypeException] = useState(false); // sublimit override
  const [savingType, setSavingType] = useState(false);

  // --- Prescription details editor ---
  // presDraft is null in view mode and holds the working copy while editing, so
  // an in-progress edit is never confused with what is stored. Kept out of the
  // evaluation's dirty/unsaved-changes machinery on purpose: this is a separate
  // record with its own Save, and a half-typed hospital name must not block the
  // evaluator from leaving the screen.
  const [presDraft, setPresDraft] = useState(null);
  const [presSaving, setPresSaving] = useState(false);
  const [presErr, setPresErr] = useState("");
  // The ICD combobox: which of the 3 slots is open, its query, and its results.
  const [icdSlot, setIcdSlot] = useState(-1);
  const [icdQuery, setIcdQuery] = useState("");
  const [icdOptions, setIcdOptions] = useState([]);
  const [icdSearching, setIcdSearching] = useState(false);
  const icdTimerRef = useRef(null);
  const [typeError, setTypeError] = useState("");
  // The wallet re-triggers the AI asynchronously after a type change, so the
  // record can still read COMPLETED for a moment. Poll on this flag until the
  // reset lands, from where aiProcessing takes over.
  const [reAdjudicating, setReAdjudicating] = useState(false);

  // `reseed` forces the evaluator form back to the server's copy even when it
  // holds unsaved edits — used when those edits are deliberately discarded.
  const load = (silent, reseed) => {
    if (!claimId) {
      setError("No claim id in the URL.");
      setLoading(false);
      return;
    }
    api.getClaimAssessment(
      claimId,
      patientId,
      (res) => {
        setRecord(res || null);
        if (res && res.walletStatus !== undefined && res.walletStatus !== null)
          setWalletStatus(String(res.walletStatus));
        // Seed the evaluator layer from the server — but never on top of unsaved
        // edits. `load(true)` also runs from the 6s poll and after a status /
        // document change, and re-seeding there would wipe marks the evaluator
        // has made but not saved yet.
        if (reseed || !dirtyRef.current) {
          const hr = (res && res.humanReview) || {};
          const isC =
            hr.isAiSuggestionCorrect === undefined
              ? null
              : hr.isAiSuggestionCorrect;
          const rv = {};
          const rc = {};
          (hr.ruleVerdicts || []).forEach((v) => {
            rv[v.target] = v.aiCorrect;
            if (v.note) rc[v.target] = v.note;
          });
          const lv = {};
          const lc = {};
          (hr.lineVerdicts || []).forEach((v) => {
            lv[v.target] = v.aiCorrect;
            if (v.note) lc[v.target] = v.note;
          });
          // Baseline first: these setStates flush synchronously (an async API
          // callback isn't batched in React 16), so a render that landed before
          // the ref was updated would compare fresh state against a stale
          // baseline and flag the untouched form as dirty.
          savedSigRef.current = reviewSignature({
            isCorrect: isC,
            reason: hr.reasonForIncorrect || "",
            ruleVerdicts: rv,
            ruleComments: rc,
            lineVerdicts: lv,
            lineComments: lc,
          });
          dirtyRef.current = false;
          setIsCorrect(isC);
          setReason(hr.reasonForIncorrect || "");
          setRuleVerdicts(rv);
          setRuleComments(rc);
          setLineVerdicts(lv);
          setLineComments(lc);
        }
        setLoading(false);
      },
      () => {
        if (!silent)
          setError(
            "Could not load the AI assessment for this claim. It may not have been processed yet.",
          );
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // Drop any in-flight ICD lookup on unmount, so a debounce that fires after the
  // screen is gone doesn't set state on an unmounted component.
  useEffect(
    () => () => {
      if (icdTimerRef.current) clearTimeout(icdTimerRef.current);
    },
    [],
  );

  // While the AI is still processing, poll quietly until the decision lands.
  // aiProcessing is computed via a module-level helper rather than an inline
  // `record && … && …` const: a top-level logical-expression const sitting
  // between two hooks trips react-hooks/rules-of-hooks' code-path analysis and
  // false-flags the later useEffects as "called conditionally".
  const aiProcessing = computeAiProcessing(record);
  useEffect(() => {
    if (!aiProcessing) return undefined;
    const t = setInterval(() => load(true), 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiProcessing, claimId]);

  // Bridge between a claim-type change and the poll above: the wallet fires the
  // re-adjudication in the background, so for a few seconds the record still
  // reads COMPLETED with the old decision. Poll faster until the reset shows up
  // (aiProcessing turns true and clears this), and give up after ~30s so a
  // trigger that never lands doesn't poll forever.
  useEffect(() => {
    if (!reAdjudicating) return undefined;
    let ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      load(true);
      if (ticks >= 10) {
        clearInterval(t);
        setReAdjudicating(false);
      }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reAdjudicating, claimId]);

  useEffect(() => {
    if (aiProcessing && reAdjudicating) setReAdjudicating(false);
  }, [aiProcessing, reAdjudicating]);

  // Claim types offered by this claim's plan — the options for the type change.
  // Keyed on the ids rather than `record` so the 6s poll doesn't refetch them.
  const typesPlanId = planIdOf(record);
  const typesPatientId = patientIdOf(record);
  useEffect(() => {
    if (!typesPlanId || !typesPatientId) return;
    api.getAllClaimTypes(
      typesPlanId,
      typesPatientId,
      (res) => setClaimTypes((res && res.reimbursementTypes) || []),
      () => setClaimTypes([]),
    );
  }, [typesPlanId, typesPatientId]);

  useEffect(() => {
    const category = record && record.category;
    if (!category) return;
    api.getClaimRules(
      category,
      (res) => setRules(res || null),
      () => {},
    );
  }, [record]);

  useEffect(() => {
    const code = record && record.accountCode;
    if (!code) return;
    api.getAccountConfigs(
      (res) => {
        const list = Array.isArray(res) ? res : (res && res.data) || [];
        const match = list.find(
          (c) =>
            c &&
            String(c.accountCode).toUpperCase() === String(code).toUpperCase(),
        );
        if (match && match.accountName) setAccountName(match.accountName);
      },
      () => {},
    );
  }, [record]);

  // Masked bank account for the claim (card item + verify/change flow).
  useEffect(() => {
    if (!record || isZeroObjectId(record.bankAccountId)) {
      setBankResp(null);
      return;
    }
    bankAccountService.fetchMaskedBankAccountForPatient(
      record.patientId,
      record.bankAccountId,
      (res) => setBankResp(res || null),
      () => {},
    );
  }, [record]);

  useEffect(() => {
    if (!record) return;
    patientService.getAndSaveDocumentToken(
      (res) => setDocToken((res && res.token) || ""),
      () => {},
    );
    const checkIds = (
      (record.assessment && record.assessment.documentChecks) ||
      []
    )
      .map((d) => d.documentId)
      .filter(Boolean);
    // Union the AI-checked ids with the wallet claim's documentList: the fallback
    // covers not-yet-processed claims, and the union surfaces documents added
    // on the fly (which land on the claim's documentList but not the AI checks).
    // Normalise to strings first: documentChecks carries documentId as a STRING
    // while the wallet's documentList is []int64, so an un-normalised Set keeps
    // "123" and 123 apart and every AI-checked document loads — and renders in
    // the viewer — twice.
    const ids = Array.from(
      new Set(
        [...(checkIds || []), ...(record.documentList || [])]
          .filter((v) => v !== null && v !== undefined && v !== "")
          .map(String),
      ),
    );
    if (!ids.length) return;
    Promise.all(
      ids.map(
        (id) =>
          new Promise((resolve) =>
            api.getDocuments(
              id,
              (res) => resolve(res),
              () => resolve(null),
            ),
          ),
      ),
    ).then((list) => setDocs((list || []).filter((d) => d && d.url)));
  }, [record]);

  // Evaluator controls are frozen in view mode and on a decided claim. The rest
  // of the screen (status, claim type, documents, bank account) stays live —
  // an approved claim still has to be movable to Processed.
  const evalReadOnly = computeEvalReadOnly(readOnly, walletStatus);
  const evalLocked = computeEvalReadOnly(false, walletStatus);

  // Unsaved evaluator edits — the current form vs. the last loaded/saved state.
  const dirty = computeDirty(
    evalReadOnly,
    {
      isCorrect: isCorrect,
      reason: reason,
      ruleVerdicts: ruleVerdicts,
      ruleComments: ruleComments,
      lineVerdicts: lineVerdicts,
      lineComments: lineComments,
    },
    savedSigRef.current,
  );
  // Mirrored onto a ref during render, not in an effect: the navigation guard
  // below reads it from a click that can land before passive effects have
  // flushed (toggle a mark, immediately hit Back), and it must already know the
  // form is dirty by then. Safe here — React 16 legacy mode, no concurrent
  // rendering to tear.
  dirtyRef.current = dirty;

  // Leaving with unsaved edits: block the navigation and ask first. Registered
  // on the router's history, so it covers every exit — the back arrow, the
  // drawer, and the browser's own Back button. The callback reads `dirtyRef`
  // rather than `dirty` so the block isn't torn down and re-registered on
  // every keystroke.
  useEffect(() => {
    const history = props.history;
    if (readOnly || !history || !history.block) return undefined;
    const unblock = history.block((location, action) => {
      // Clean, or already answered the dialog — let it through.
      if (bypassGuardRef.current || !dirtyRef.current) return undefined;
      setLeaveTo({ location: location, action: action });
      return false; // held back; the dialog decides what happens next
    });
    return unblock;
  }, [props.history, readOnly]);

  // Same guard for a tab close / refresh, which the router never sees.
  useEffect(() => {
    if (readOnly) return undefined;
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [readOnly]);

  // Always land on the new AI claims dashboard (ClaimAIDashboard, not the legacy
  // claims-dashboard being phased out). Never history.goBack(): a pop goes to
  // whatever the previous history entry happens to be, and most Bridge navigation
  // (Helper.navigateTo, used by the drawer and the Miscellaneous tiles) *replaces*
  // entries instead of pushing them, so the entry under this screen is often the
  // Bridge home page rather than the dashboard. `dashboardSearch` carries the list's
  // page number and filters, which the dashboard seeds its state from, so the view
  // comes back exactly as the user left it without popping.
  const goToDashboard = () => {
    if (!props.history) return;
    props.history.replace({
      pathname: "/miscellaneous/reimbursement-dashboard",
      search:
        (props.location &&
          props.location.state &&
          props.location.state.dashboardSearch) ||
        "",
    });
  };
  // --- Prescription details editing -----------------------------------------
  const startPresEdit = () => {
    const src = (record && record.effectivePrescriptionDetails) || {};
    // Every coding is carried, however many there are. Slots used to be fixed at
    // three ("Level 1/2/3"), which silently DROPPED a fourth code on save.
    const codings = (src.icdCodings || []).filter(Boolean);
    setPresErr("");
    setPresDraft({
      hospitalName: src.hospitalName || "",
      hospitalAddress: src.hospitalAddress || "",
      state: src.state || "",
      city: src.city || "",
      pinCode: src.pinCode || "",
      doctorName: src.doctorName || "",
      doctorQualification: src.doctorQualification || "",
      chiefComplaintText: src.chiefComplaintText || "",
      diagnosisText: src.diagnosisText || "",
      chiefComplaints: src.chiefComplaints || [],
      diagnoses: src.diagnoses || [],
      icdCodings: codings,
      // Carried through untouched — not editable here, but the override replaces
      // the WHOLE object, so omitting them would erase them on the first save.
      chiefComplaintCodings: src.chiefComplaintCodings || [],
    });
  };
  const cancelPresEdit = () => {
    setPresDraft(null);
    setPresErr("");
    setIcdSlot(-1);
  };
  const setPresField = (k, v) => setPresDraft((d) => ({ ...d, [k]: v }));

  // Debounced ICD lookup. Two characters is too little to be useful and returns
  // a huge slice of a 45k-row master, so wait for three.
  const runIcdSearch = (text) => {
    setIcdQuery(text);
    if (icdTimerRef.current) clearTimeout(icdTimerRef.current);
    if (!text || text.trim().length < 3) {
      setIcdOptions([]);
      setIcdSearching(false);
      return;
    }
    setIcdSearching(true);
    icdTimerRef.current = setTimeout(() => {
      api.searchIcd(
        text.trim(),
        (res) => {
          setIcdSearching(false);
          setIcdOptions(
            Array.isArray(res) ? res.filter((o) => o && o.code) : [],
          );
        },
        () => {
          setIcdSearching(false);
          setIcdOptions([]);
        },
      );
    }, 300);
  };

  // Picking replaces the slot's code. `matchedTerm` (the diagnosis the AI read)
  // is preserved so the audit line still says which wording this answers, but
  // `specificity` is cleared — it described the AI's own choice and says nothing
  // about the code a human just substituted.
  //
  // The search response has no separate title field: the ICD service builds
  // `label` as `code + " " + title` and appends " (synonyms)" only when the row
  // has common_terms. Dropping the leading code recovers the title; the trailing
  // parenthetical is deliberately NOT stripped, because plenty of real titles
  // carry parentheses of their own ("Essential (primary) hypertension") and
  // removing the last one would corrupt them. Only ~0.4% of the master has
  // synonyms at all, and `code` — the value an insurer receives — is exact
  // either way.
  const chooseIcd = (slot, opt) => {
    setPresDraft((d) => {
      const codings = d.icdCodings.slice();
      // No opt = the row's clear button: remove it outright rather than leave a
      // hole, so the list stays exactly what will be saved.
      if (!opt) {
        codings.splice(slot, 1);
        return { ...d, icdCodings: codings };
      }
      const prev = codings[slot] || {};
      const entry = {
        code: opt.code,
        title: String(opt.label || "").replace(/^\s*\S+\s*/, ""),
        matchedTerm: prev.matchedTerm || "",
        zylaIcdCode: opt.value || "",
        source: "MATCHED",
        specificity: prev.code === opt.code ? prev.specificity || "" : "",
      };
      // slot === length is the trailing blank row, i.e. "add another".
      if (slot >= codings.length) codings.push(entry);
      else codings[slot] = entry;
      return { ...d, icdCodings: codings };
    });
    setIcdSlot(-1);
    setIcdQuery("");
    setIcdOptions([]);
  };

  // The ONLY place the prescription capture is written back. On success the
  // record is reloaded so the card shows the server's resolved value (override
  // over AI) rather than the local draft.
  const savePresDetails = () => {
    if (!presDraft) return;
    setPresSaving(true);
    setPresErr("");
    const payload = {
      prescriptionDetails: {
        ...presDraft,
        icdCodings: presDraft.icdCodings.filter(Boolean),
      },
      editedBy: getEvaluator(),
    };
    api.updateClaimPrescriptionDetails(
      claimId,
      payload,
      () => {
        setPresSaving(false);
        setPresDraft(null);
        setIcdSlot(-1);
        load(true);
      },
      (err) => {
        setPresSaving(false);
        // Surfaces the service's own message — a bad PIN or a malformed ICD code
        // comes back as a 400 naming the offending value.
        setPresErr(apiErrorText(err, "Failed to save prescription details."));
      },
    );
  };

  const setRuleVerdict = (id, v) => setRuleVerdicts((p) => ({ ...p, [id]: v }));
  const setLineVerdict = (t, v) => setLineVerdicts((p) => ({ ...p, [t]: v }));

  // Build the full evaluation payload from current state. Each verdict carries
  // its ✓/✗ (aiCorrect, null when unset) and its comment (note). Targets with
  // only a comment (no verdict) are still included so the note persists.
  const buildEvaluationPayload = (rc, lc) => {
    const ruleTargets = Array.from(
      new Set(Object.keys(ruleVerdicts).concat(Object.keys(rc))),
    );
    const lineTargets = Array.from(
      new Set(Object.keys(lineVerdicts).concat(Object.keys(lc))),
    );
    return {
      isAiSuggestionCorrect: isCorrect,
      reasonForIncorrect: reason,
      ruleVerdicts: ruleTargets.map((target) => ({
        target,
        aiCorrect: ruleVerdicts[target] == null ? null : ruleVerdicts[target],
        note: rc[target] || "",
      })),
      lineVerdicts: lineTargets.map((target) => ({
        target,
        aiCorrect: lineVerdicts[target] == null ? null : lineVerdicts[target],
        note: lc[target] || "",
      })),
      evaluatedBy: getEvaluator(),
    };
  };

  // The ONLY place the evaluator layer is written to the backend. `onDone` runs
  // after a successful save (used by "Save & leave" in the unsaved-changes
  // dialog) — call as save(), never as an onClick handler directly, or the
  // click event would be passed in as the callback.
  const save = (onDone) => {
    const sig = reviewSignature({
      isCorrect: isCorrect,
      reason: reason,
      ruleVerdicts: ruleVerdicts,
      ruleComments: ruleComments,
      lineVerdicts: lineVerdicts,
      lineComments: lineComments,
    });
    setSaving(true);
    setSaved(false);
    setError("");
    api.submitClaimEvaluation(
      claimId,
      buildEvaluationPayload(ruleComments, lineComments),
      () => {
        // New baseline before the setStates (which flush synchronously here),
        // so the render they trigger already sees the form as clean — and so a
        // save-then-leave doesn't re-trip the navigation guard.
        savedSigRef.current = sig;
        dirtyRef.current = false;
        setSaving(false);
        setSaved(true);
        if (onDone) onDone();
      },
      (err) => {
        setSaving(false);
        // The claim service refuses the write once the claim has been decided
        // (409). Show its reason rather than a generic retry prompt, and pull
        // the form back in line with what's actually stored.
        setError(
          apiErrorText(err, "Failed to save the evaluation. Please retry."),
        );
        load(true, true);
      },
    );
  };

  // Resolve the held-back decision: either save the evaluation first, or throw
  // the unsaved edits away (reloading the server's copy, so the form the lock
  // freezes is the one that was actually stored). Then reopen the status dialog.
  const resolvePendingDecision = (discard) => {
    const id = pendingDecision;
    setPendingDecision(null);
    if (!id) return;
    if (discard) {
      dirtyRef.current = false;
      load(true, true);
      openStatusDialog(id, true);
      return;
    }
    save(() => openStatusDialog(id, true));
  };

  // Replay the navigation the unsaved-changes guard held back.
  const leaveNow = () => {
    const target = leaveTo;
    setLeaveTo(null);
    bypassGuardRef.current = true; // don't block the replayed navigation
    if (!target || !props.history) return;
    if (target.action === "POP") props.history.goBack();
    else if (target.action === "REPLACE")
      props.history.replace(target.location);
    else props.history.push(target.location);
  };

  // Comment popup: open with the target's existing comment; "Done" keeps it in
  // the form only. It used to PUT the whole evaluation right here, which meant
  // one comment silently committed every other unsaved mark on the screen —
  // changes stuck even when the evaluator walked away without pressing Save.
  // Now nothing leaves the browser until the header's Save.
  const openComment = (kind, target, label) => {
    const existing =
      kind === "rule" ? ruleComments[target] : lineComments[target];
    setCommentTarget({ kind: kind, target: target, label: label });
    setCommentDraft(existing || "");
  };

  const applyComment = () => {
    if (!commentTarget) return;
    const text = commentDraft.trim();
    if (commentTarget.kind === "rule")
      setRuleComments((p) => ({ ...p, [commentTarget.target]: text }));
    else setLineComments((p) => ({ ...p, [commentTarget.target]: text }));
    setCommentTarget(null);
  };

  // Open the status-change dialog for a target status id (from the card's Status
  // dropdown). No-op if it's the current status. Prefills the approved amount for
  // Approve (AI-eligible amount, else the filed amount) like the old dashboard.
  // Approve dialog: fetch the current available claim amount from the wallet's
  // sublimit calculation (same body the old dashboard posts). Caps the approved
  // amount and is shown as a read-only field.
  const fetchAvailableAmount = () => {
    if (!record) return;
    setCurrAvailAmt(null);
    setFetchingAvail(true);
    api.fetchCurrAvailableClaimAmt(
      {
        patientId: record.patientId,
        claimedFor: record.claimedFor,
        planId: record.planId,
        claimAmount: record.claimAmount,
        claimTypeId: record.reimbursementTypeId,
        blockedAmount: record.eligibleClaimAmount,
        ignorePlanExpiry: true,
        claimId: record.claimId || "",
      },
      (res) => {
        setCurrAvailAmt(res ? res.eligibleAmount : null);
        setFetchingAvail(false);
      },
      (err) => {
        setCurrAvailAmt(null);
        setFetchingAvail(false);
        // eslint-disable-next-line no-console
        console.error("fetchCurrAvailableClaimAmt failed", err);
      },
    );
  };

  // Reject dialog: predefined rejection reasons (wallet admin endpoint). Fetched
  // once and cached; the reason is required alongside the remarks.
  const fetchRejectReasons = () => {
    if (reasonList.length) return;
    setFetchingReasons(true);
    api.rejectReason(
      (res) => {
        setReasonList(Array.isArray(res) ? res : []);
        setFetchingReasons(false);
      },
      (err) => {
        setFetchingReasons(false);
        // eslint-disable-next-line no-console
        console.error("rejectReason list failed", err);
      },
    );
  };

  const openStatusDialog = (value, force) => {
    const id = Number(value);
    if (!id || id === Number(walletStatus)) return;
    // Deciding the claim locks its evaluation for good, so unsaved review edits
    // would be silently swallowed by the transition. Ask first.
    if (!force && isDecidedStatus(id) && dirtyRef.current) {
      setPendingDecision(id);
      return;
    }
    // Seed the remark and reason from the AI's member guidance, but ONLY when
    // ops is taking the action the AI suggested: a hold instruction ("here's
    // what we need") would be wrong on a rejection, and vice versa. Ops can
    // always edit or clear what is seeded.
    const guidance = (record && record.assessment && record.assessment.memberGuidance) || {};
    const aiDecision = String(
      (record && record.assessment && record.assessment.decision) || "",
    ).toUpperCase();
    const seedable =
      (id === 6 && aiDecision === "ON_HOLD") || (id === 3 && aiDecision === "REJECTED");
    const seedText = seedable ? String(guidance.instructionText || "") : "";
    setDlgRemarks(seedText ? plainToHtml(seedText) : "");
    setDlgEditorState(
      seedText
        ? EditorState.createWithContent(ContentState.createFromText(seedText))
        : EditorState.createEmpty(),
    );
    setDlgAmount("");
    setDlgTxnId("");
    setDlgReason(seedable ? String(guidance.rejectReason || "") : "");
    setDlgUploadAgain(seedable && id === 6 ? !!guidance.needsReupload : false);
    setDlgConfirm(false);
    setDlgError("");
    setStatusDialog(id);
    // Approve is a two-step flow that shows the current available amount.
    if (id === 2) fetchAvailableAmount();
    // Reject needs the predefined reason list for its dropdown.
    if (id === 3) fetchRejectReasons();
  };

  // Document-type correction. No dialog and no re-adjudication — it only
  // records how the claim arrived. The row is refreshed from the server rather
  // than patched locally so the value shown is the one that was stored.
  const saveDocumentType = (value) => {
    const dt = Number(value);
    if (
      !record ||
      !record.walletClaimObjId ||
      !dt ||
      dt === Number(record.documentType)
    )
      return;
    setSavingDocType(true);
    api.updateClaim(
      record.walletClaimObjId,
      { walletId: record.walletId, documentTypeUpdate: dt },
      () => {
        setSavingDocType(false);
        EventService.emit("showError", { message: "Document type updated." });
        load(true);
      },
      (err) => {
        setSavingDocType(false);
        EventService.emit("showError", {
          message: (err && err.message) || "Failed to update document type.",
        });
      },
    );
  };

  // Claim-type change: opening the dialog fetches the sublimit available under
  // the TARGET type (same eligibility endpoint the Approve dialog uses, with the
  // new claimTypeId), so the evaluator sees whether the claim still fits before
  // committing to the change.
  const openTypeDialog = (value) => {
    const id = Number(value);
    if (!id || !record || id === Number(record.reimbursementTypeId)) return;
    setTypeReason("");
    // The wallet overwrites ignoreReimbursementLimit with whatever this request
    // sends, so start from the claim's current flag — otherwise changing the type
    // would silently revoke an exception the claim already carries.
    setTypeException(!!record.ignoreReimbursementLimit);
    setTypeError("");
    setTypeAvail(null);
    setTypeDialog(id);
    setFetchingTypeAvail(true);
    api.fetchCurrAvailableClaimAmt(
      {
        patientId: record.patientId,
        claimedFor: record.claimedFor,
        planId: record.planId,
        claimAmount: record.claimAmount,
        claimTypeId: id,
        // Preview only. The claim's eligibleClaimAmount is blocked against its
        // CURRENT category; the eligibility endpoint adds blockedAmount onto the
        // TARGET category's sublimit without checking it belongs there, which
        // inflates the new type's availability across a category change. The
        // claim isn't blocked under the new type yet, so pass 0 to see the target
        // type's true remaining sublimit. (The Approve dialog keeps eligibleClaimAmount
        // because it recomputes under the SAME category.)
        blockedAmount: 0,
        ignorePlanExpiry: true,
        claimId: record.claimId || "",
      },
      (res) => {
        setTypeAvail(res ? res.eligibleAmount : null);
        setFetchingTypeAvail(false);
      },
      () => {
        // Non-fatal: the wallet enforces the sublimit on submit anyway. The
        // dialog just can't show the available amount up front.
        setTypeAvail(null);
        setFetchingTypeAvail(false);
      },
    );
  };

  const typeNameOf = (typeId) => {
    const t = claimTypes.find((x) => Number(x.typeId) === Number(typeId));
    return (t && t.typeName) || "";
  };

  // The claim no longer fits the target type's remaining sublimit. This is shown
  // as a warning but does NOT block the change: the wallet (V2 admin update) only
  // rejects a re-type when the target sublimit is fully exhausted (available <= 0),
  // not on a partial over-limit — matching the old dashboard, which let the change
  // through and surfaced any hard rejection as a server error.
  const typeOverLimit =
    typeAvail !== null &&
    record &&
    Number(record.claimAmount) > Number(typeAvail);

  const applyClaimTypeChange = () => {
    const id = Number(typeDialog);
    if (!record || !record.walletClaimObjId || !id) {
      setTypeError("Claim is not available in the wallet.");
      return;
    }
    const employeeUserId = getEmployeeUserId();
    if (!employeeUserId) {
      setTypeError(
        "Could not identify the logged-in user. Please sign in again.",
      );
      return;
    }
    setTypeError("");
    setSavingType(true);
    api.updateClaimType(
      record.walletClaimObjId,
      {
        walletId: record.walletId,
        reimbursementTypeUpdate: {
          typeId: id,
          // Feature disabled: never carry an exception through a type change.
          ignoreReimbursementLimit: SHOW_SUBLIMIT_EXCEPTION
            ? typeException
            : false,
          employeeUserId: employeeUserId,
          reason: typeReason.trim(),
        },
      },
      () => {
        setSavingType(false);
        setTypeDialog(null);
        // The claim service drops the old assessment AND its review, so clear
        // the evaluator form too — otherwise marks made against the old
        // category linger (and count as unsaved edits) on the new assessment.
        savedSigRef.current = EMPTY_REVIEW_SIG;
        dirtyRef.current = false;
        setIsCorrect(null);
        setReason("");
        setRuleVerdicts({});
        setRuleComments({});
        setLineVerdicts({});
        setLineComments({});
        setSaved(false);
        // The wallet re-triggers the AI under the new type, so the screen goes
        // back to the processing state on its own — reAdjudicating just makes
        // it poll until that lands.
        setReAdjudicating(true);
        load(true);
        EventService.emit("showError", {
          message: "Claim type updated — the AI is re-assessing this claim.",
        });
      },
      (err) => {
        setSavingType(false);
        // eslint-disable-next-line no-console
        console.error("updateClaimType failed", err);
        setTypeError(
          (err && err.message) || "Failed to update the claim type.",
        );
      },
    );
  };

  // Remarks field shared by every status dialog. A <div>, not <label>: a <label>
  // re-fires clicks onto its labelable control, which steals focus from the
  // editor's toolbar buttons. On the confirm step the dialog is read-only, so the
  // authored HTML is rendered instead of an editor nobody can type into.
  const remarksField = (label, placeholder) => (
    <div className="sd-field">
      <span>{label}</span>
      {dlgConfirm ? (
        <div
          className="sd-remarks-ro"
          dangerouslySetInnerHTML={{
            __html: remarkHasText(dlgRemarks) ? dlgRemarks : "<p>—</p>",
          }}
        />
      ) : (
        <div className="sd-remarks-editor">
          <Editor
            editorState={dlgEditorState}
            onEditorStateChange={(newState) => {
              setDlgEditorState(newState);
              setDlgRemarks(
                draftToHtml(convertToRaw(newState.getCurrentContent())),
              );
            }}
            stripPastedStyles={false}
            placeholder={placeholder}
            toolbar={REMARKS_TOOLBAR}
            wrapperClassName="sd-editor-wrapper"
            toolbarClassName="sd-editor-toolbar"
            editorClassName="sd-editor-body"
          />
        </div>
      )}
    </div>
  );

  // Required-field gate per status, matching the wallet's own validation:
  //  Approve → amount > 0; Processed → transaction id; Reject / On Hold /
  //  Provisional / Resubmitted → a non-empty comment.
  const statusDialogValid = () => {
    const id = Number(statusDialog);
    if (id === 2) return Number(dlgAmount) > 0;
    if (id === 4) return dlgTxnId.trim().length > 0;
    // Reject needs both a reason and remarks (the wallet requires both).
    if (id === 3)
      return dlgReason.trim().length > 0 && remarkHasText(dlgRemarks);
    if (id === 6 || id === 7 || id === 8) return remarkHasText(dlgRemarks);
    return true;
  };

  // Build the status-specific statusUpdate payload and apply it. Shape + field
  // names match the wallet's BridgeTicketEvent:
  //   { walletId, statusUpdate: { id, employeeName, comment,
  //       approvedClaimAmount? | rejectReason? | transactionId? | uploadDocumentsAgain? } }
  const applyStatusUpdate = () => {
    const id = Number(statusDialog);
    if (!record || !record.walletClaimObjId || !id) {
      setDlgError("Claim status is not available (wallet not reachable).");
      return;
    }
    // The wallet stores the remark as-is and the status history renders it as
    // HTML, so send the editor's markup — but collapse a markup-only remark
    // ("<p></p>") to "" rather than persisting an empty paragraph.
    const comment = remarkHasText(dlgRemarks) ? dlgRemarks.trim() : "";
    const su = { id: id, employeeName: getEvaluator(), comment: comment };
    if (id === 2) su.approvedClaimAmount = Number(dlgAmount) || 0;
    // Reject: the selected reason (rejectReason) + remarks (comment). The wallet
    // requires both to be non-empty.
    if (id === 3) su.rejectReason = dlgReason.trim();
    if (id === 4) su.transactionId = dlgTxnId.trim();
    if (id === 6) su.uploadDocumentsAgain = dlgUploadAgain;
    setDlgError("");
    setSavingStatus(true);
    api.updateClaim(
      record.walletClaimObjId,
      { walletId: record.walletId, statusUpdate: su },
      () => {
        setSavingStatus(false);
        setWalletStatus(String(id));
        setStatusDialog(null);
        load(true); // refresh updatedAt + status history
      },
      (err) => {
        setSavingStatus(false);
        // Surface the wallet's message inside the dialog instead of the
        // page-bottom error (which sits behind the modal overlay).
        // eslint-disable-next-line no-console
        console.error("updateClaim failed", err);
        setDlgError((err && err.message) || "Failed to update claim status.");
      },
    );
  };

  // Refresh the masked bank account after the verify/change dialogs act on it.
  const fetchMaskedBankForClaim = (pid, accId) => {
    if (isZeroObjectId(accId)) return;
    bankAccountService.fetchMaskedBankAccountForPatient(
      pid,
      accId,
      (res) => setBankResp(res || null),
      () => {},
    );
  };
  const closeBankDialogs = () => {
    setOpenBankVerify(false);
    setOpenBankChange(false);
    setOpenBankSuccess(false);
  };

  const header = (right) => (
    <div className="claim-detail-header">
      <div className="claim-detail-header-left">
        <div className="claim-detail-back" onClick={goToDashboard}>
          <BackArrow />
        </div>
        <h2 className="claim-detail-title">Claim details</h2>
      </div>
      <div className="claim-detail-header-right">{right}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="claim-detail-container">
        {header(null)}
        <div className="claim-detail-loading">Loading…</div>
      </div>
    );
  }
  if (error && !record) {
    return (
      <div className="claim-detail-container">
        {header(null)}
        <div className="claim-detail-loading">{error}</div>
      </div>
    );
  }

  const a = (record && record.assessment) || {};
  const decision = a.decision || "—";
  // Claims whose type isn't processed by AI (non-medical service types, excluded
  // accounts) are recorded as SKIPPED — render the claim, but hide the AI-specific
  // sections (suggestion, evaluator, rule verdicts) since there's no assessment.
  const aiSkipped = String(decision).toUpperCase() === "SKIPPED";
  // No AI record yet (backend returned a wallet "shell"): show the claim details
  // only. hideAI folds this together with SKIPPED — both hide the AI-specific UI.
  const aiUnprocessed =
    String(record.status || "").toUpperCase() === "NOT_PROCESSED";
  const hideAI = aiSkipped || aiUnprocessed;
  // Coverage card: prefer the per-plan inclusions/exclusions actually applied to
  // this claim; fall back to the category defaults from the rules catalog.
  const covIncluded =
    record && record.inclusions && record.inclusions.length
      ? record.inclusions
      : (rules && rules.included) || [];
  const covExcluded =
    record && record.exclusions && record.exclusions.length
      ? record.exclusions
      : (rules && rules.excluded) || [];
  // The server resolves override-vs-AI, so the screen never picks between the two
  // stored copies. Null for claims adjudicated before the capture existed.
  const presc = record && record.effectivePrescriptionDetails;
  // Why a term went uncoded, in ops language. The distinction matters: an
  // unavailable master is a broken deployment, a missing candidate is curation
  // work on the ICD table, and a declined match is a prompt problem. Showing one
  // generic "not coded" for all three sends people looking in the wrong place.
  const ICD_REASON = {
    MASTER_UNAVAILABLE: "ICD service unreachable",
    NO_CANDIDATES: "nothing matching in the ICD master",
    MODEL_DECLINED: "no confident match",
    MODEL_ERROR: "ICD lookup failed",
    INVALID_PICK: "match rejected as invalid",
  };

  // ICD codings render as a LIST, one entry per coded term. There are no fixed
  // "Level 1/2/3" slots: those were only ever the insurer's column names, and a
  // consultation carries as many ICDs as the doctor actually recorded — usually
  // one. An entry the workflow could not code still shows the doctor's wording,
  // so a failed lookup is visible as something to fix rather than a blank row.
  const icdList = (list) => {
    const rows = (list || []).filter(Boolean);
    if (!rows.length) return "\u2014";
    return (
      <ul className="presc-icd-list">
        {rows.map((c, i) => (
          <li key={i}>
            {c.source === "MATCHED" && c.code ? (
              <>
                <b>{c.code}</b>
                {c.title}
              </>
            ) : (
              <span className="presc-uncoded">
                {(c.matchedTerm || "\u2014") +
                  " \u2014 not coded" +
                  (ICD_REASON[c.reason]
                    ? " (" + ICD_REASON[c.reason] + ")"
                    : "")}
              </span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const ruleEvals = a.ruleEvaluations || [];
  const lines = a.lineItems || [];
  const docChecks = a.documentChecks || [];
  const flaggedDoc = docChecks.find((d) => d.suspected);
  // Possible re-filing: the matched earlier claim(s), strongest match first.
  const dupVerdict = ruleEvals.find(
    (r) =>
      r.ruleId === DUPLICATE_RULE_ID &&
      String(r.status).toUpperCase() === "FAIL",
  );
  const dupClaimIds = (dupVerdict && dupVerdict.matchedClaimIds) || [];
  // The earlier claim opens view-only in a new tab: the reviewer is mid-way
  // through this one, and navigating away would drop unsaved verdicts.
  const priorClaimHref = (id) =>
    "/miscellaneous/claims-dashboard/ai-review/" +
    id +
    (record && record.patientId ? "/" + record.patientId : "") +
    "?view=1";
  const priorClaimLinks = (ids) =>
    ids.map((id, i) => (
      <React.Fragment key={id}>
        {i > 0 ? ", " : ""}
        <a href={priorClaimHref(id)} target="_blank" rel="noopener noreferrer">
          {id}
        </a>
      </React.Fragment>
    ));
  const memberGuidance = a.memberGuidance || {};
  const copyGuidance = () => {
    const text = String(memberGuidance.instructionText || "");
    if (!text) return;
    // navigator.clipboard needs a secure context; fall back so the button is
    // never dead on an http origin.
    const done = () => {
      setGuidanceCopied(true);
      window.setTimeout(() => setGuidanceCopied(false), 1500);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done, () => {});
      return;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      done();
    } finally {
      document.body.removeChild(ta);
    }
  };
  const reasonChips = Array.from(
    new Set(
      ruleEvals
        .filter((r) => String(r.status).toUpperCase() === "FAIL" && r.blocking)
        .map((r) => r.remark || r.label || r.ruleId),
    ),
  ).slice(0, 6);

  const verdictChip = (status) => {
    const s = String(status || "").toUpperCase();
    if (s === "PASS") return <span className="verdict-chip pass">PASS</span>;
    if (s === "FAIL")
      return <span className="verdict-chip fail">MISMATCH</span>;
    return <span className="verdict-chip na">NA</span>;
  };
  const yn = (b) => (
    <span className={"yn " + (b ? "yes" : "no")}>{b ? "Yes" : "No"}</span>
  );
  const humanToggle = (cur, onSet, hasComment, onComment) => (
    <span className={"human-toggle" + (evalReadOnly ? " readonly" : "")}>
      <span
        className={"ht-btn yes" + (cur === true ? " on" : "")}
        onClick={evalReadOnly ? undefined : () => onSet(true)}
      >
        <TickIcon />
      </span>
      <span
        className={"ht-btn no" + (cur === false ? " on" : "")}
        onClick={evalReadOnly ? undefined : () => onSet(false)}
      >
        <CrossIcon />
      </span>
      {/* Frozen (view mode or a decided claim): the comment stays viewable when
          one exists; hidden only when there's nothing to view. */}
      {(!evalReadOnly || hasComment) && (
        <span
          className={"ht-btn comment" + (hasComment ? " has-comment" : "")}
          onClick={onComment}
          title={
            evalReadOnly
              ? "View comment"
              : hasComment
                ? "Edit comment"
                : "Add comment"
          }
        >
          <ChatGlyph />
        </span>
      )}
    </span>
  );

  // Attach a document to this claim on the fly: upload it to the document service,
  // then append the returned id to the wallet claim's documentList. There's no cap
  // on the document count; the viewer refreshes so the new file shows immediately.
  const onAddDocument = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = null; // allow re-picking the same file
    if (!file || !record || !record.walletClaimObjId) return;
    if (file.size > 20000000) {
      EventService.emit("showError", {
        message:
          'File "' + file.name + '" is too large. Max allowed size is 20 MB.',
      });
      return;
    }
    setUploadingDoc(true);
    const document = {
      title: file.name,
      description: "",
      url: "",
      type: 12,
      format: getDocumentFormat(extOf(file.name)),
      status: 4,
    };
    api.uploadDocument(
      file,
      file.name,
      document,
      (res) => {
        const newId = res && res.id;
        if (!newId) {
          setUploadingDoc(false);
          EventService.emit("showError", {
            message: "Document upload failed.",
          });
          return;
        }
        // Append the new document id to the claim (documentListUpdate appends).
        api.updateClaim(
          record.walletClaimObjId,
          { walletId: record.walletId, documentListUpdate: [newId] },
          () => {
            setUploadingDoc(false);
            EventService.emit("showError", {
              message: "Document added to the claim.",
            });
            load(true); // refresh — the union effect surfaces the new file
          },
          (err) => {
            setUploadingDoc(false);
            EventService.emit("showError", {
              message:
                (err && err.message) ||
                "Could not attach the document to the claim.",
            });
          },
        );
      },
      (err) => {
        setUploadingDoc(false);
        EventService.emit("showError", {
          message:
            (err && err.message) ||
            "Something went wrong while uploading the document.",
        });
      },
      record.patientId,
    );
  };

  // Image docs (jpg/png/…) can't be shown in an <iframe> — the dev1 files proxy
  // sends X-Frame-Options, so Chrome blocks the frame ("refused to connect").
  // Render those with <img> (not frame-gated); keep the iframe for PDFs.
  const isImageDoc = (url) =>
    /\.(jpe?g|png|gif|webp|bmp)(\?|#|$)/i.test(url || "") ||
    /\/(jpe?g|png|image)s?\//i.test(url || "");

  const currentDoc = docs[docIndex];
  // The document on its own, without the viewer's #page/zoom fragment — this is
  // what the "open in new tab" control hands to the browser.
  const docHref =
    currentDoc && docToken ? currentDoc.url + "?token=" + docToken : "";
  const openDocInNewTab = () => {
    if (!docHref) return;
    window.open(docHref, "_blank", "noopener,noreferrer");
  };
  const pdfSrc = currentDoc
    ? currentDoc.url +
      "?token=" +
      docToken +
      "#page=" +
      pdfPage +
      "&zoom=" +
      zoom
    : "";

  const statusPillClass = WALLET_STATUS_PILL[Number(walletStatus)] || "pending";

  // Statuses an evaluator can move the claim to (each opens its own dialog).
  // Always keep the current status in the list so the dropdown displays it even
  // when it isn't an actionable transition (e.g. Received / Draft).
  // Same items the old dashboard showed — all eight statuses stay visible and the
  // invalid ones render disabled (Received is always disabled; Appealed is only
  // reachable from Rejected).
  const STATUS_ACTIONS = [1, 2, 3, 4, 5, 6, 7, 8];
  const statusOptions = (() => {
    const cur = Number(walletStatus);
    // Show every action status, but DISABLE (grey out) the ones the wallet won't
    // accept from the current status — and the current status itself. Mirrors the
    // old dashboard, where invalid transitions stay visible but disabled instead
    // of being hidden.
    const opts = STATUS_ACTIONS.map((id) => ({
      value: String(id),
      label: WALLET_STATUS[id],
      disabled: !cur || !canMoveStatus(id, cur),
    }));
    // If the current status isn't an action status (e.g. Received / Draft /
    // Appealed), prepend it disabled so the control still displays it.
    if (cur && STATUS_ACTIONS.indexOf(cur) === -1) {
      opts.unshift({
        value: String(cur),
        label: WALLET_STATUS[cur],
        disabled: true,
      });
    }
    return opts;
  })();

  // Wallet ticketEvents = the claim's status-history timeline. Show newest-first
  // (the wallet appends chronologically). Guard against a missing createdAt so an
  // undated event sorts to the bottom rather than throwing.
  const ts = (e) => {
    const t = e && e.createdAt ? new Date(e.createdAt).getTime() : NaN;
    return isNaN(t) ? -Infinity : t;
  };
  const statusHistory = ((record && record.ticketEvents) || [])
    .slice()
    .sort((a, b) => ts(b) - ts(a));

  // Claim type and Document type both render as plain text with an inline orange
  // pencil (the same affordance the bank account row uses). The dropdown only
  // replaces the text once the pencil is clicked, and opens on mount, so a
  // correction stays one click plus one pick.
  const canEditType =
    !readOnly &&
    claimTypes.length > 0 &&
    record.reimbursementTypeId &&
    canEditClaimType(walletStatus);

  // Prefer the claim's live wallet type over the AI record's category: right
  // after a type change the record still carries the old category until the
  // re-adjudication lands.
  const claimTypeLabel =
    typeNameOf(record.reimbursementTypeId) || record.category;

  // Picking a new claim type opens the confirmation dialog, which is what
  // re-adjudicates the claim under the new category.
  const claimTypeCell = !canEditType ? (
    claimTypeLabel
  ) : editingField === "claimType" ? (
    <ThemedDropdown
      className="td-compact claim-type-dd"
      value={String(record.reimbursementTypeId)}
      onChange={openTypeDialog}
      onClose={() => setEditingField(null)}
      autoOpen
      placeholder="—"
      options={claimTypes.map((t) => ({
        value: String(t.typeId),
        label: t.typeName,
        disabled: Number(t.typeId) === Number(record.reimbursementTypeId),
      }))}
    />
  ) : (
    <span className="editable-field">
      {claimTypeLabel}
      <button
        className="field-edit"
        title="Change claim type"
        onClick={() => setEditingField("claimType")}
      >
        <PencilIcon />
      </button>
    </span>
  );

  // Document type: 1=Softcopy, 2=Hardcopy (0/absent = unspecified). Editable as
  // an ops correction — how a claim was submitted is recorded by hand at filing
  // time and is routinely wrong. Unlike a claim-type change this doesn't
  // re-adjudicate anything, so it saves straight away with no confirmation step.
  const docTypeLabel = savingDocType
    ? "Saving…"
    : DOCUMENT_TYPE_LABEL[record.documentType] || "—";

  const documentTypeCell = readOnly ? (
    DOCUMENT_TYPE_LABEL[record.documentType] || "—"
  ) : editingField === "documentType" ? (
    <ThemedDropdown
      className="td-compact claim-type-dd"
      value={record.documentType ? String(record.documentType) : ""}
      onChange={saveDocumentType}
      onClose={() => setEditingField(null)}
      autoOpen
      placeholder={docTypeLabel}
      options={DOCUMENT_TYPE_OPTIONS}
      disabled={savingDocType}
    />
  ) : (
    <span className="editable-field">
      {docTypeLabel}
      <button
        className="field-edit"
        title="Change document type"
        onClick={() => setEditingField("documentType")}
      >
        <PencilIcon />
      </button>
    </span>
  );

  return (
    <div className="claim-detail-container">
      {header(
        <>
          {/* Read-only status pill — the actual status change now happens from
              the Status control inside the Claim details card (below). */}
          <span className={"status-pill " + statusPillClass}>
            <span className="status-dot" />
            {WALLET_STATUS[Number(walletStatus)] || "—"}
          </span>
          {record.updatedAt ? (
            <span className="updated-pill">
              <Clock /> Updated {fmtDate(record.updatedAt)}
            </span>
          ) : null}
          {/* Save = the one and only commit point for the evaluator review;
              marks and comments live in the form until it's pressed. Only
              meaningful when there's an AI assessment. Hidden in view-only. */}
          {!hideAI && !evalReadOnly && (
            <>
              {dirty && <span className="unsaved-pill">Unsaved changes</span>}
              <button
                className="save-btn"
                onClick={() => save()}
                disabled={saving}
              >
                {saving ? "Saving…" : saved && !dirty ? "Saved ✓" : "Save"}
              </button>
            </>
          )}
        </>,
      )}

      <div className="claim-detail-content">
        {(aiProcessing || reAdjudicating) && (
          <div className="claim-processing-banner">
            <span className="cpb-dot" />{" "}
            {reAdjudicating
              ? "Claim type changed — the AI is re-assessing this claim under the new type."
              : "AI is processing this claim — the assessment will appear here automatically."}
          </div>
        )}
        {record && String(record.status || "").toUpperCase() === "FAILED" && (
          <div className="claim-processing-banner failed">
            AI processing failed for this claim. Re-file or trigger processing
            again to retry.
          </div>
        )}
        {aiSkipped && (
          <div className="claim-processing-banner failed">
            This claim type isn’t processed by AI — showing claim details only.
          </div>
        )}
        {aiUnprocessed && (
          <div className="claim-processing-banner">
            AI hasn’t processed this claim yet — showing claim details only.
          </div>
        )}
        {/* Claim details */}
        <div className="claim-card">
          <div className="section-header-row">
            <h3 className="section-title">Claim details</h3>
            {record.walletId ? (
              <button
                className="wallet-details-btn spacer"
                onClick={() => setWalletDialogOpen(true)}
              >
                <WalletGlyph /> Wallet details
              </button>
            ) : null}
          </div>
          <div className="field-grid">
            {[
              ["Claim ID", record.claimId],
              ["Patient", record.patientName || record.patientId || "—"],
              // Claimed for: "Self" when filed for the account holder (claimedFor 0,
              // dropped by omitempty), else the member's name + relation.
              [
                "Claimed for",
                record.claimedFor
                  ? (record.claimedForName || "—") +
                    (record.claimedForRelationName
                      ? " (" + record.claimedForRelationName + ")"
                      : "")
                  : "Self",
              ],
              [
                "Account",
                accountName
                  ? accountName + " (" + record.accountCode + ")"
                  : record.accountCode,
              ],
              [
                "Plan",
                record.planName ||
                  (record.planId ? "Plan " + record.planId : "—"),
              ],
              // Status closes the first row — it is the 6th column, sitting under
              // the Wallet details button, and is edited here rather than in the
              // header. Selecting a new status opens its confirmation dialog.
              record.walletClaimObjId
                ? [
                    "Status",
                    <ThemedDropdown
                      className="td-compact status-field-dd"
                      value={String(walletStatus)}
                      onChange={openStatusDialog}
                      placeholder="—"
                      options={statusOptions}
                      disabled={readOnly}
                    />,
                    "",
                    "status-field",
                  ]
                : null,
              // Three amounts sit side by side and are easy to confuse:
              //   Invoice Amount  — what the user filed the claim for, straight off
              //                     the filing form. Deliberately NOT
              //                     assessment.invoiceAmount, which is the total the
              //                     AI read off the uploaded bill and which can
              //                     legitimately differ (see claim_prompt.py). The
              //                     status dialog shows this same value under this
              //                     same label — keep the two in step.
              //   Amount blocked  — eligibleClaimAmount, held against the wallet
              //   Approved amount — what ops actually approved (0 before approval)
              ["Invoice Amount", rupees(record.claimAmount)],
              ["Amount blocked", rupees(record.eligibleClaimAmount)],
              ["Approved amount", rupees(record.approvedClaimAmount)],
              ["Claim type", claimTypeCell],
              ["Document type", documentTypeCell],
              // Bank account opens its own row: .bank-cell pins it back to column
              // 1 so the masked number, verification badge and verify button get
              // the width they need.
              !isZeroObjectId(record.bankAccountId)
                ? [
                    "Bank account",
                    <>
                      <span className="bank-num">
                        {bankResp
                          ? ((bankResp.branchInfo &&
                              bankResp.branchInfo.bankName) ||
                              "Bank") +
                            " - " +
                            maskedBankNum(bankResp.last4, bankResp.accNumLength)
                          : "—"}
                      </span>
                      {bankResp ? (
                        <span
                          className={
                            "bank-badge " +
                            (BANK_STATUS_CLASS[bankResp.verificationStatus] ||
                              "unverified")
                          }
                        >
                          {BANK_STATUS_LABEL[bankResp.verificationStatus] ||
                            "Unverified"}
                        </span>
                      ) : null}
                      {!readOnly && (
                        <button
                          className="bank-edit"
                          title="View / verify bank account"
                          onClick={() => setOpenBankVerify(true)}
                        >
                          <PencilIcon />
                        </button>
                      )}
                    </>,
                    "bank-cell",
                    "bank-field",
                  ]
                : null,
            ]
              .filter(Boolean)
              .map(([k, v, cellClass, valueClass]) => (
                <div key={k} className={cellClass || undefined}>
                  <div className="field-label">{k}</div>
                  <div
                    className={
                      "field-value" + (valueClass ? " " + valueClass : "")
                    }
                  >
                    {v}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* AI suggestion + Evaluator — hidden when the claim wasn't AI-processed */}
        {!hideAI && (
          <div className="claim-row">
            <div className="claim-card">
              <div className="section-header-row">
                <h3 className="section-title">AI suggestion</h3>
                <span
                  className={
                    "decision-badge " + (DECISION_BADGE[decision] || "skipped")
                  }
                >
                  {DECISION_LABEL[decision] || decision}
                </span>
                {a.confidencePct ? (
                  <span className="confidence-pill spacer">
                    AI confidence: {a.confidencePct}%
                  </span>
                ) : null}
                {flaggedDoc ? (
                  <span className="aiimage-pill">
                    AI-image {flaggedDoc.confidencePct}%
                  </span>
                ) : null}
                {dupClaimIds.length > 0 ? (
                  <span className="dup-pill">
                    Possible duplicate of {priorClaimLinks(dupClaimIds)}
                  </span>
                ) : null}
              </div>
              <p className="summary-text">
                {a.decisionSummary || a.extractedSummary || "—"}
              </p>
              <div className="reason-chips">
                {reasonChips.map((c, i) => (
                  <span key={i} className="reason-chip">
                    {c}
                  </span>
                ))}
              </div>
              {/* What ops SENDS the member, as opposed to decisionSummary above,
                  which names rules and is written for a reviewer. Generated
                  deterministically by the workflow; ops can edit it in the
                  status dialog, which this prefills. */}
              {memberGuidance.instructionText ? (
                <div className="member-guidance">
                  <div className="mg-head">
                    <span className="mg-title">Suggested message to member</span>
                    <button
                      type="button"
                      className="mg-copy"
                      onClick={copyGuidance}
                    >
                      {guidanceCopied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="mg-text">{memberGuidance.instructionText}</div>
                  {memberGuidance.rejectReason ? (
                    <div className="mg-reason">
                      Reason: {memberGuidance.rejectReason}
                    </div>
                  ) : null}
                  {(memberGuidance.reasonCodes || []).length > 0 ? (
                    <div className="mg-codes">
                      {memberGuidance.reasonCodes.map((c, i) => (
                        <span key={i} className="mg-code">
                          {c.toLowerCase().replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="claim-card">
              <h3 className="section-title">Evaluator comment</h3>
              {evalLocked && (
                <div className="eval-locked-note">
                  This claim is {WALLET_STATUS[Number(walletStatus)]} — its
                  evaluation is locked and can no longer be edited.
                </div>
              )}
              <p className="evaluation-question">
                Is the AI suggestion correct?
              </p>
              <div
                className={
                  "evaluation-buttons" + (evalReadOnly ? " readonly" : "")
                }
              >
                <button
                  className={
                    "eval-btn" + (isCorrect === true ? " active-correct" : "")
                  }
                  onClick={evalReadOnly ? undefined : () => setIsCorrect(true)}
                >
                  Correct
                </button>
                <button
                  className={
                    "eval-btn" +
                    (isCorrect === false ? " active-incorrect" : "")
                  }
                  onClick={evalReadOnly ? undefined : () => setIsCorrect(false)}
                >
                  Incorrect
                </button>
              </div>
              {isCorrect === false && (
                <div className="evaluation-reason">
                  <label>
                    Reason for incorrect suggestion
                    <span className="req-star">*</span>
                  </label>
                  <div className="reason-input">
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={evalReadOnly}
                    />
                    <span className="reason-hint">max. 500 words</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Document review */}
        <div className="claim-card">
          <h3 className="section-title">Document review</h3>
          {/* Keep the two-column layout even with no AI data, so the document
              viewer stays the same size as on AI-processed claims; the AI
              column shows a disabled-style placeholder instead of collapsing. */}
          <div className="doc-review-grid">
            <div className="pdf-panel">
              <div className="pdf-toolbar">
                <div
                  style={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  {docs.length > 1 && (
                    <ThemedDropdown
                      className="td-compact pdf-doc-dropdown"
                      value={docIndex}
                      onChange={(v) => {
                        setDocIndex(v);
                        setPdfPage(1);
                        setRotation(0);
                      }}
                      options={docs.map((d, i) => ({
                        value: i,
                        label: d.title || "Document " + (d.id || i + 1),
                      }))}
                    />
                  )}
                  <span className="pdf-doc-name">
                    {currentDoc
                      ? currentDoc.title || "Document " + (currentDoc.id || "")
                      : "No document"}
                  </span>
                  {/* Add a document on the fly (Bridge only, any number). */}
                  {!readOnly && (
                    <React.Fragment>
                      <button
                        className="pdf-ctrl-btn pdf-upload-btn"
                        onClick={() =>
                          docUploadRef.current && docUploadRef.current.click()
                        }
                        disabled={uploadingDoc}
                        title="Add document"
                      >
                        {uploadingDoc ? (
                          "…"
                        ) : (
                          <img
                            src={uploadIcon}
                            alt="upload"
                            style={{ width: 18, height: 18, display: "block" }}
                          />
                        )}
                      </button>
                      <input
                        ref={docUploadRef}
                        type="file"
                        hidden
                        accept={DOC_ACCEPT_TYPES}
                        onChange={onAddDocument}
                      />
                    </React.Fragment>
                  )}
                </div>
                <div className="pdf-controls">
                  <button
                    className="pdf-ctrl-btn"
                    onClick={() => setZoom((z) => Math.max(50, z - 25))}
                    title="Zoom out"
                  >
                    −
                  </button>
                  <span className="pdf-ctrl-label">{zoom}%</span>
                  <button
                    className="pdf-ctrl-btn"
                    onClick={() => setZoom((z) => Math.min(300, z + 25))}
                    title="Zoom in"
                  >
                    +
                  </button>
                  {currentDoc && isImageDoc(currentDoc.url) ? (
                    <button
                      className="pdf-ctrl-btn"
                      onClick={() => setRotation((r) => (r + 90) % 360)}
                      title="Rotate 90°"
                    >
                      ⟳
                    </button>
                  ) : (
                    <React.Fragment>
                      <button
                        className="pdf-ctrl-btn"
                        onClick={() => setPdfPage((p) => Math.max(1, p - 1))}
                        title="Previous page"
                      >
                        ‹
                      </button>
                      <span className="pdf-ctrl-label">Page {pdfPage}</span>
                      <button
                        className="pdf-ctrl-btn"
                        onClick={() => setPdfPage((p) => p + 1)}
                        title="Next page"
                      >
                        ›
                      </button>
                    </React.Fragment>
                  )}
                  {/* Pop the document out of the embedded viewer — useful for
                      anything the iframe renders too small to read. */}
                  <button
                    className="pdf-ctrl-btn pdf-popout-btn"
                    onClick={openDocInNewTab}
                    disabled={!docHref}
                    title="Open document in a new tab"
                  >
                    ↗
                  </button>
                </div>
              </div>
              {currentDoc && docToken ? (
                isImageDoc(currentDoc.url) ? (
                  <div
                    className="pdf-image-wrap"
                    onMouseDown={(e) => {
                      const el = e.currentTarget;
                      el.dataset.pan = "1";
                      el.dataset.px = e.pageX;
                      el.dataset.py = e.pageY;
                      el.dataset.sl = el.scrollLeft;
                      el.dataset.st = el.scrollTop;
                    }}
                    onMouseMove={(e) => {
                      const el = e.currentTarget;
                      if (el.dataset.pan !== "1") return;
                      el.scrollLeft =
                        Number(el.dataset.sl) -
                        (e.pageX - Number(el.dataset.px));
                      el.scrollTop =
                        Number(el.dataset.st) -
                        (e.pageY - Number(el.dataset.py));
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.dataset.pan = "0";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.dataset.pan = "0";
                    }}
                  >
                    <img
                      key={docIndex + "-" + zoom}
                      alt="claim document"
                      src={currentDoc.url + "?token=" + docToken}
                      style={{
                        width: zoom + "%",
                        transform: "rotate(" + rotation + "deg)",
                        transformOrigin: "center center",
                      }}
                      draggable={false}
                    />
                  </div>
                ) : (
                  <iframe
                    key={docIndex + "-" + zoom + "-" + pdfPage}
                    title="claim-document"
                    src={pdfSrc}
                  />
                )
              ) : (
                <div className="pdf-empty">
                  {docs.length === 0
                    ? "No document to preview"
                    : "Loading document…"}
                </div>
              )}
            </div>

            {hideAI ? (
              <div className="doc-fields">
                <div className="doc-fields-placeholder">
                  {aiUnprocessed
                    ? "AI has not processed this claim"
                    : "This claim type isn’t processed by AI"}
                </div>
              </div>
            ) : (
              <div className="doc-fields">
                <div className="doc-fields-scroll">
                  <table className="claim-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>In document</th>
                        <th>System record</th>
                        <th>AI evaluation</th>
                        <th className="col-center">Human evaluation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ruleEvals.map((r, i) => (
                        <tr key={r.ruleId || i}>
                          <td className="td-field">{r.label || r.ruleId}</td>
                          <td>{r.observed || "—"}</td>
                          <td>
                            {r.systemRecord || "—"}
                            {r.ruleId === DUPLICATE_RULE_ID &&
                            (r.matchedClaimIds || []).length > 0 ? (
                              <div className="dup-links">
                                Open {priorClaimLinks(r.matchedClaimIds)}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            {r.ruleId === DUPLICATE_RULE_ID &&
                            String(r.status).toUpperCase() === "FAIL" ? (
                              <span className="verdict-chip fail">DUPLICATE</span>
                            ) : (
                              verdictChip(r.status)
                            )}
                          </td>
                          <td className="col-center">
                            {humanToggle(
                              ruleVerdicts[r.ruleId],
                              (v) => setRuleVerdict(r.ruleId, v),
                              !!ruleComments[r.ruleId],
                              () =>
                                openComment(
                                  "rule",
                                  r.ruleId,
                                  r.label || r.ruleId,
                                ),
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {lines.length > 0 && (
                    <React.Fragment>
                      <div
                        style={{
                          margin: "16px 0 8px",
                          fontWeight: 700,
                          fontSize: 12,
                          color: "#364153",
                        }}
                      >
                        Billed items
                      </div>
                      <table className="claim-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Amount</th>
                            <th>Prescribed</th>
                            <th>Covered</th>
                            <th className="col-center">Human evaluation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((li, i) => {
                            const target = li.description || "line-" + i;
                            return (
                              <tr key={target + i}>
                                <td className="td-field">{li.description}</td>
                                <td>{rupees(li.amount)}</td>
                                <td>{yn(li.prescribed)}</td>
                                <td>{yn(li.covered)}</td>
                                <td className="col-center">
                                  {humanToggle(
                                    lineVerdicts[target],
                                    (v) => setLineVerdict(target, v),
                                    !!lineComments[target],
                                    () =>
                                      openComment(
                                        "line",
                                        target,
                                        li.description || target,
                                      ),
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </React.Fragment>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Prescription details — captured for insurers; hidden behind
            SHOW_PRESCRIPTION_DETAILS until the ops-facing view is signed off.
            Editable at ANY claim status (unlike the evaluation, which freezes
            once the claim is decided): these are facts about a document that
            stay live with the insurer long after payout, and the write is
            additive — the AI's own extraction is never overwritten. */}
        {SHOW_PRESCRIPTION_DETAILS && presc && (
          <div className="claim-card">
            <div className="section-header-row">
              <h3 className="section-title">Prescription details</h3>
              {!readOnly && !presDraft && (
                <button className="presc-edit-btn" onClick={startPresEdit}>
                  Edit
                </button>
              )}
            </div>
            <div className="muted">
              Read from the prescription
              {record.prescriptionDetailsOverride
                ? " and corrected by " +
                  (record.prescriptionDetailsEditedBy || "an evaluator")
                : ""}
              .
            </div>

            {!presDraft ? (
              <div className="field-grid">
                {[
                  ["Hospital / Clinic", presc.hospitalName],
                  ["Address", presc.hospitalAddress],
                  ["State", presc.state],
                  ["City", presc.city],
                  ["PIN code", presc.pinCode],
                  // The prescriber. Qualification is the degree line, and the
                  // signature rule now passes on a letterhead that carries it,
                  // so a reviewer needs to see what was actually read.
                  ["Doctor", presc.doctorName],
                  ["Qualification", presc.doctorQualification],
                  // Chief complaint and diagnosis stay separate rows on purpose:
                  // a complaint is what the patient reported, a diagnosis is what
                  // the doctor concluded, and only the latter is ICD-coded.
                  ["Chief complaint", presc.chiefComplaintText],
                  ["Diagnosis", presc.diagnosisText],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="field-label">{k}</div>
                    <div className="field-value">{v || "\u2014"}</div>
                  </div>
                ))}
                {/* Diagnosis codes are what an insurer receives. Complaint codes
                    are shown separately and are never reported as diagnoses. */}
                <div className="presc-wide">
                  <div className="field-label">ICD 10 codes · diagnosis</div>
                  <div className="field-value">{icdList(presc.icdCodings)}</div>
                </div>
                <div className="presc-wide">
                  <div className="field-label">
                    ICD 10 codes · chief complaint
                  </div>
                  <div className="field-value">
                    {icdList(presc.chiefComplaintCodings)}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="presc-edit-grid">
                  {[
                    ["hospitalName", "Hospital / Clinic", false],
                    ["hospitalAddress", "Address", true],
                    ["state", "State", false],
                    ["city", "City", false],
                    ["pinCode", "PIN code", false],
                    ["doctorName", "Doctor", false],
                    ["doctorQualification", "Qualification", false],
                    ["chiefComplaintText", "Chief complaint", false],
                    ["diagnosisText", "Diagnosis", true],
                  ].map(([key, label, wide]) => (
                    <div
                      className={"sd-field" + (wide ? " presc-wide" : "")}
                      key={key}
                    >
                      <span>{label}</span>
                      <input
                        value={presDraft[key]}
                        onChange={(e) => setPresField(key, e.target.value)}
                        placeholder={
                          key === "pinCode" ? "6 digits, or leave blank" : ""
                        }
                      />
                    </div>
                  ))}

                  {/* One picker per existing code, plus a trailing blank row to
                      add another. Not fixed "levels" — a consultation carries as
                      many ICDs as the doctor recorded. */}
                  {presDraft.icdCodings.concat([null]).map((chosen, n) => (
                    <div className="sd-field presc-icd" key={n}>
                      <span>
                        {chosen
                          ? "ICD 10 code " + (n + 1)
                          : presDraft.icdCodings.length
                            ? "Add another ICD 10 code"
                            : "ICD 10 code"}
                      </span>
                      {chosen ? (
                        <div className="presc-icd-chosen">
                          <span className="presc-icd-code">{chosen.code}</span>
                          <span>{chosen.title}</span>
                          <button
                            className="presc-icd-clear"
                            title="Remove"
                            onClick={() => chooseIcd(n, null)}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <input
                          value={icdSlot === n ? icdQuery : ""}
                          placeholder="Search the ICD-10 master…"
                          onFocus={() => {
                            setIcdSlot(n);
                            setIcdQuery("");
                            setIcdOptions([]);
                          }}
                          onChange={(e) => runIcdSearch(e.target.value)}
                        />
                      )}
                      {icdSlot === n && !chosen && (
                        <ul className="presc-icd-menu">
                          {icdSearching && (
                            <li className="presc-icd-empty">Searching…</li>
                          )}
                          {!icdSearching && icdQuery.trim().length < 3 && (
                            <li className="presc-icd-empty">
                              Type at least 3 characters
                            </li>
                          )}
                          {!icdSearching &&
                            icdQuery.trim().length >= 3 &&
                            icdOptions.length === 0 && (
                              <li className="presc-icd-empty">No match</li>
                            )}
                          {icdOptions.map((o) => (
                            <li
                              key={o.value || o.code}
                              onMouseDown={() => chooseIcd(n, o)}
                            >
                              <b>{o.code}</b>{" "}
                              {String(o.label || "").replace(/^\s*\S+\s*/, "")}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
                <div className="presc-hint">
                  Pick codes from the master rather than typing them, and choose
                  the most specific code the prescription actually supports —
                  never one that asserts a detail the doctor did not write.
                </div>
                <div className="presc-actions">
                  <button
                    className="sd-btn primary"
                    disabled={presSaving}
                    onClick={savePresDetails}
                  >
                    {presSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    className="sd-btn ghost"
                    disabled={presSaving}
                    onClick={cancelPresEdit}
                  >
                    Cancel
                  </button>
                  {presErr ? (
                    <span className="presc-err">{presErr}</span>
                  ) : null}
                </div>
              </>
            )}
          </div>
        )}

        {/* Coverage rules */}
        {rules && (
          <div className="claim-card">
            <h3 className="section-title">
              Coverage rules · {record.category}
            </h3>
            <div className="muted">
              Inclusions &amp; exclusions applied to this claim (required docs:{" "}
              {(rules.requiredDocs || []).join(", ") || "—"})
            </div>
            <div className="coverage-grid">
              <div>
                <span className="coverage-pill included">
                  INCLUDED · {covIncluded.length}
                </span>
                <ul className="coverage-list">
                  {covIncluded.map((x, i) => (
                    <li key={i} className="inc">
                      <span className="cov-bullet" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="coverage-pill excluded">
                  EXCLUDED · {covExcluded.length}
                </span>
                <ul className="coverage-list">
                  {covExcluded.map((x, i) => (
                    <li key={i} className="exc">
                      <span className="cov-bullet" />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Status history */}
        <div className="claim-card">
          <h3 className="section-title">Status history</h3>
          {statusHistory.length === 0 ? (
            <div className="muted">No status history for this claim yet.</div>
          ) : (
            <ul className="history-list">
              {statusHistory.map((ev, i) => (
                <li
                  key={i}
                  className={"history-entry" + (i === 0 ? " current" : "")}
                >
                  <span className="history-dot" />
                  <div className="history-body">
                    <div className="history-head">
                      <span className="history-title">
                        {ev.name || WALLET_STATUS[ev.id] || "Update"}
                      </span>
                      {ev.createdAt ? (
                        <span className="history-date">
                          {fmtDate(ev.createdAt)}
                        </span>
                      ) : null}
                    </div>
                    {remarkHasText(ev.comment) ? (
                      // Remarks are authored as rich text, so render the markup
                      // rather than printing raw "<p>…</p>" at the reader.
                      <div
                        className="history-note"
                        dangerouslySetInnerHTML={{ __html: ev.comment }}
                      />
                    ) : null}
                    <div className="history-by">{ev.createdBy || "System"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error ? (
          <div className="muted" style={{ color: "#e44437" }}>
            {error}
          </div>
        ) : null}
      </div>

      {statusDialog && (
        <div
          className="status-dialog-overlay"
          onClick={() => !savingStatus && setStatusDialog(null)}
        >
          <div className="status-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="status-dialog-head">
              <h3>
                {Number(statusDialog) === 2
                  ? dlgConfirm
                    ? "Check carefully before approving the claim"
                    : "Approve claim"
                  : Number(statusDialog) === 3
                    ? dlgConfirm
                      ? "Check carefully before rejecting the claim"
                      : "Reject claim"
                    : Number(statusDialog) === 4
                      ? dlgConfirm
                        ? "Check carefully before processing the claim"
                        : "Process claim"
                      : Number(statusDialog) === 6
                        ? dlgConfirm
                          ? "Check carefully before changing the status to on hold"
                          : "Hold claim"
                        : Number(statusDialog) === 7
                          ? "Provisional approved claim"
                          : Number(statusDialog) === 8
                            ? "Resubmitted claim"
                            : "Move to “" +
                              WALLET_STATUS[Number(statusDialog)] +
                              "”"}
              </h3>
              <button
                className="status-dialog-close"
                onClick={() => setStatusDialog(null)}
                disabled={savingStatus}
              >
                ×
              </button>
            </div>

            <div className="status-dialog-body">
              {Number(statusDialog) === 2 ? (
                <>
                  {dlgConfirm && (
                    <div className="sd-warn">
                      * Please note once the amount is deducted it cannot be
                      undone.
                    </div>
                  )}
                  <div className="sd-grid2">
                    <div className="sd-ro">
                      <span>Invoice amount</span>
                      <div className="sd-ro-val">
                        {rupees(record.claimAmount)}
                      </div>
                    </div>
                    <div className="sd-ro highlight">
                      <span>AI suggestion</span>
                      <div className="sd-ro-val">
                        {a.eligibleAmount != null
                          ? rupees(a.eligibleAmount)
                          : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="sd-grid2">
                    <div className="sd-ro">
                      <span>Amount blocked</span>
                      <div className="sd-ro-val">
                        {rupees(record.eligibleClaimAmount)}
                      </div>
                    </div>
                    <div className="sd-ro">
                      <span>Current available claim amount</span>
                      <div className="sd-ro-val">
                        {fetchingAvail
                          ? "…"
                          : currAvailAmt === null
                            ? "—"
                            : rupees(currAvailAmt)}
                      </div>
                    </div>
                  </div>

                  {dlgConfirm ? (
                    <div className="sd-ro">
                      <span>Approved claim amount</span>
                      <div className="sd-ro-val">
                        {rupees(Number(dlgAmount))}
                      </div>
                    </div>
                  ) : (
                    <label className="sd-field">
                      <span>Approved claim amount *</span>
                      <input
                        type="number"
                        min="0"
                        value={dlgAmount}
                        onChange={(e) => {
                          const v = e.target.value;
                          const max =
                            currAvailAmt === null
                              ? Infinity
                              : Number(currAvailAmt);
                          if (v === "" || Number(v) <= max) setDlgAmount(v);
                        }}
                        disabled={fetchingAvail || currAvailAmt === null}
                        placeholder={
                          currAvailAmt === null
                            ? "Enter the approved claim amount"
                            : "Enter the approved claim amount here (upto " +
                              currAvailAmt +
                              ")"
                        }
                      />
                    </label>
                  )}

                  {remarksField(
                    "Remarks",
                    "Remarks (if any, for internal purposes)",
                  )}
                </>
              ) : Number(statusDialog) === 3 ? (
                <>
                  {dlgConfirm && (
                    <div className="sd-warn">
                      * Please note once rejected it cannot be changed.
                    </div>
                  )}
                  {/* A <div>, not <label>: a <label> re-fires clicks onto its
                      labelable control (ThemedDropdown's trigger button), which
                      would toggle the just-opened menu back closed/open. */}
                  <div className="sd-field">
                    <span>Reason for rejection *</span>
                    <ThemedDropdown
                      className="td-compact"
                      value={dlgReason}
                      onChange={setDlgReason}
                      placeholder={
                        fetchingReasons ? "Loading reasons…" : "Select a reason"
                      }
                      options={reasonList.map((r) => ({
                        value: r.reason,
                        label: r.reason,
                      }))}
                      disabled={dlgConfirm || fetchingReasons}
                    />
                  </div>
                  {remarksField(
                    "Remarks *",
                    "Remarks (to be shared with the user)",
                  )}
                </>
              ) : (
                <>
                  {Number(statusDialog) === 4 && dlgConfirm && (
                    <div className="sd-warn">
                      * Please note once processed, the transaction ID cannot be
                      changed.
                    </div>
                  )}
                  {Number(statusDialog) === 6 && dlgConfirm && (
                    <div className="sd-warn">
                      * If the “Checkmark to Activate Upload Documents” option
                      is selected, the user will be allowed to submit supporting
                      documents one more time. Once the documents are
                      successfully resubmitted, the claim status will
                      automatically update to “Resubmitted”.
                    </div>
                  )}

                  {Number(statusDialog) === 4 && (
                    <label className="sd-field">
                      <span>Transaction ID *</span>
                      <input
                        type="text"
                        value={dlgTxnId}
                        onChange={(e) =>
                          setDlgTxnId(e.target.value.slice(0, 50))
                        }
                        placeholder="Enter the payout transaction reference"
                        disabled={dlgConfirm}
                      />
                    </label>
                  )}

                  {remarksField(
                    [6, 7, 8].indexOf(Number(statusDialog)) !== -1
                      ? "Remarks *"
                      : "Remarks",
                    "Remarks (to be shared with the user)",
                  )}

                  {Number(statusDialog) === 6 && (
                    <label className="sd-check">
                      <input
                        type="checkbox"
                        checked={dlgUploadAgain}
                        onChange={(e) => setDlgUploadAgain(e.target.checked)}
                        disabled={dlgConfirm}
                      />
                      <span>
                        Activate upload documents button for this claim.
                      </span>
                    </label>
                  )}
                </>
              )}

              {dlgError && <div className="sd-error">{dlgError}</div>}
            </div>

            <div className="status-dialog-foot">
              <button
                className="sd-btn ghost"
                onClick={() => {
                  // On the confirm step of a two-step flow, Back returns to step 1.
                  if (
                    dlgConfirm &&
                    [2, 3, 4, 6].indexOf(Number(statusDialog)) !== -1
                  ) {
                    setDlgConfirm(false);
                  } else {
                    setStatusDialog(null);
                  }
                }}
                disabled={savingStatus}
              >
                {dlgConfirm && [2, 3, 4, 6].indexOf(Number(statusDialog)) !== -1
                  ? "Back"
                  : "Cancel"}
              </button>
              <button
                className="sd-btn primary"
                disabled={
                  savingStatus ||
                  !statusDialogValid() ||
                  (Number(statusDialog) === 2 && fetchingAvail)
                }
                onClick={() => {
                  const id = Number(statusDialog);
                  // Approve, Reject, Processed and On Hold use a second-step confirm.
                  if ([2, 3, 4, 6].indexOf(id) !== -1 && !dlgConfirm) {
                    setDlgConfirm(true);
                    return;
                  }
                  applyStatusUpdate();
                }}
              >
                {savingStatus
                  ? "Saving…"
                  : Number(statusDialog) === 2
                    ? dlgConfirm
                      ? "Deduct amount from wallet"
                      : "Continue"
                    : Number(statusDialog) === 3
                      ? dlgConfirm
                        ? "Reject the claim"
                        : "Continue"
                      : Number(statusDialog) === 4
                        ? dlgConfirm
                          ? "Mark as processed"
                          : "Continue"
                        : Number(statusDialog) === 6
                          ? "Continue"
                          : [7, 8].indexOf(Number(statusDialog)) !== -1
                            ? "Save"
                            : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim-type change (the old dashboard's "Edit claim type"). Reuses the
          status-dialog shell. Changing the type re-adjudicates the claim, which
          the warning makes explicit before the evaluator commits. */}
      {typeDialog && (
        <div
          className="status-dialog-overlay"
          onClick={() => !savingType && setTypeDialog(null)}
        >
          <div className="status-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="status-dialog-head">
              <h3>Change claim type</h3>
              <button
                className="status-dialog-close"
                onClick={() => setTypeDialog(null)}
                disabled={savingType}
              >
                ×
              </button>
            </div>

            <div className="status-dialog-body">
              <div className="sd-warn">
                * The AI assessment and any review of it are discarded — the
                claim is re-assessed under the new claim type.
              </div>

              <div className="sd-grid2">
                <div className="sd-ro">
                  <span>Current claim type</span>
                  <div className="sd-ro-val">
                    {typeNameOf(record.reimbursementTypeId) ||
                      record.category ||
                      "—"}
                  </div>
                </div>
                <div className="sd-ro highlight">
                  <span>New claim type</span>
                  <div className="sd-ro-val">
                    {typeNameOf(typeDialog) || "—"}
                  </div>
                </div>
              </div>

              <div className="sd-grid2">
                <div className="sd-ro">
                  <span>Claim amount</span>
                  <div className="sd-ro-val">{rupees(record.claimAmount)}</div>
                </div>
                <div className="sd-ro">
                  <span>Available under new type</span>
                  <div className="sd-ro-val">
                    {fetchingTypeAvail
                      ? "…"
                      : typeAvail === null
                        ? "—"
                        : rupees(typeAvail)}
                  </div>
                </div>
              </div>

              {/* Over the target type's sublimit: the wallet rejects the change
                  unless an exception is applied, so make it an explicit choice.
                  The box also shows when the claim already carries an exception,
                  so it stays visible (and checked) rather than being dropped. */}
              {typeOverLimit && (
                <div className="sd-error">
                  * The claim amount exceeds the sublimit available under the
                  selected claim type.
                </div>
              )}
              {SHOW_SUBLIMIT_EXCEPTION && (typeOverLimit || typeException) && (
                <label className="sd-check">
                  <input
                    type="checkbox"
                    checked={typeException}
                    onChange={(e) => setTypeException(e.target.checked)}
                  />
                  Sublimit exception applied
                </label>
              )}

              <label className="sd-field">
                <span>Reason *</span>
                <textarea
                  value={typeReason}
                  onChange={(e) => setTypeReason(e.target.value)}
                  placeholder="Why is the claim type being changed? (for internal purposes)"
                  maxLength={500}
                />
              </label>

              {typeError && <div className="sd-error">{typeError}</div>}
            </div>

            <div className="status-dialog-foot">
              <button
                className="sd-btn ghost"
                onClick={() => setTypeDialog(null)}
                disabled={savingType}
              >
                Cancel
              </button>
              <button
                className="sd-btn primary"
                disabled={savingType || fetchingTypeAvail || !typeReason.trim()}
                onClick={applyClaimTypeChange}
              >
                {savingType ? "Saving…" : "Change claim type"}
              </button>
            </div>
          </div>
        </div>
      )}

      <WalletDetailsDialog
        open={walletDialogOpen}
        onClose={() => setWalletDialogOpen(false)}
        patientId={record.patientId}
        planId={record.planId}
        walletId={record.walletId}
        patientName={record.patientName || record.patientId}
        accountName={accountName || record.accountCode}
      />

      {/* Bank account verify + change flow — the existing helper dialogs, driven
          by a menuFor built from this claim (patientId, bankAccountId, wallet
          claim obj id). getAllClaims → load(true) refreshes the claim. */}
      {openBankVerify && (
        <Modal
          open={openBankVerify}
          onClose={closeBankDialogs}
          disableBackdropClick={true}
        >
          <BankAccountVerification
            bankAccountResponse={bankResp}
            fetchMaskedBankAccountForPatient={fetchMaskedBankForClaim}
            getAllClaims={() => load(true)}
            handleCloseModal={closeBankDialogs}
            setOpenBankAccountVerificationDialog={setOpenBankVerify}
            setOpenChangeBankAccountMiddlewareDialog={setOpenBankChange}
            setIsBankAccountSuccessScreen={setOpenBankSuccess}
            isBankAccountSuccessScreen={openBankSuccess}
            setBankAccountSuccessScreenHeading={setBankSuccessHeading}
            setBankAccountSuccessScreenOnClickDoneCallback={
              setBankSuccessDoneCb
            }
            menuFor={{
              patientId: record.patientId,
              bankAccountId: record.bankAccountId,
              id: record.walletClaimObjId,
            }}
          />
        </Modal>
      )}

      {openBankSuccess && (
        <Modal
          open={openBankSuccess}
          onClose={closeBankDialogs}
          disableBackdropClick={true}
        >
          <BankAccountSuccessScreen
            heading={bankSuccessHeading}
            onClickDone={() => {
              setOpenBankSuccess(false);
              setOpenBankVerify(true);
            }}
            handleCloseModal={() => {
              setOpenBankSuccess(false);
              setOpenBankVerify(true);
            }}
          />
        </Modal>
      )}

      {openBankChange && (
        <Modal
          open={openBankChange}
          disableBackdropClick={true}
          onClose={() => {
            setOpenBankChange(false);
            setOpenBankVerify(true);
          }}
        >
          <ChangeAccountForClaimMiddleware
            handleCloseModal={() => {
              setOpenBankChange(false);
              setOpenBankVerify(true);
            }}
            fetchMaskedBankAccountForPatient={fetchMaskedBankForClaim}
            getAllClaims={() => load(true)}
            setOpenChangeBankAccountMiddlewareDialog={setOpenBankChange}
            setOpenBankAccountVerificationDialog={setOpenBankVerify}
            operation={"update"}
            menuFor={{
              patientId: record.patientId,
              bankAccountId: record.bankAccountId,
              id: record.walletClaimObjId,
            }}
            setIsBankAccountSuccessScreen={setOpenBankSuccess}
            isBankAccountSuccessScreen={openBankSuccess}
            setBankAccountSuccessScreenHeading={setBankSuccessHeading}
          />
        </Modal>
      )}

      {/* Unsaved evaluator edits + a decision that would lock them out. */}
      {pendingDecision && (
        <div
          className="status-dialog-overlay"
          onClick={() => !saving && setPendingDecision(null)}
        >
          <div className="status-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="status-dialog-head">
              <h3>Save your evaluation first?</h3>
              <button
                className="status-dialog-close"
                onClick={() => setPendingDecision(null)}
                disabled={saving}
              >
                ×
              </button>
            </div>
            <div className="status-dialog-body">
              <div className="sd-warn">
                * Moving this claim to “{WALLET_STATUS[Number(pendingDecision)]}
                ” locks its evaluation — it can’t be edited afterwards. Your
                evaluation has unsaved changes.
              </div>
            </div>
            <div className="status-dialog-foot">
              <button
                className="sd-btn ghost"
                onClick={() => resolvePendingDecision(true)}
                disabled={saving}
              >
                Discard &amp; continue
              </button>
              <button
                className="sd-btn primary"
                onClick={() => resolvePendingDecision(false)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved evaluator edits + an attempt to leave the screen. Nothing has
          been written at this point, so the choice is genuinely discard vs save. */}
      {leaveTo && (
        <div
          className="status-dialog-overlay"
          onClick={() => !saving && setLeaveTo(null)}
        >
          <div className="status-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="status-dialog-head">
              <h3>Unsaved changes</h3>
              <button
                className="status-dialog-close"
                onClick={() => setLeaveTo(null)}
                disabled={saving}
              >
                ×
              </button>
            </div>
            <div className="status-dialog-body">
              <div className="sd-warn">
                * Your evaluation of this claim hasn’t been saved. Leaving now
                discards it.
              </div>
            </div>
            <div className="status-dialog-foot">
              <button
                className="sd-btn ghost"
                onClick={leaveNow}
                disabled={saving}
              >
                Discard &amp; leave
              </button>
              <button
                className="sd-btn primary"
                onClick={() => save(leaveNow)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save & leave"}
              </button>
            </div>
          </div>
        </div>
      )}

      {commentTarget && (
        <div
          className="status-dialog-overlay"
          onClick={() => setCommentTarget(null)}
        >
          <div className="status-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="status-dialog-head">
              <h3>
                Comment <span className="cd-sub">{commentTarget.label}</span>
              </h3>
              <button
                className="status-dialog-close"
                onClick={() => setCommentTarget(null)}
              >
                ×
              </button>
            </div>
            <div className="status-dialog-body">
              <label className="sd-field comment-field">
                <span>{evalReadOnly ? "Comment" : "Add a comment"}</span>
                <textarea
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  placeholder="Write your comment here…"
                  maxLength={1000}
                  readOnly={evalReadOnly}
                />
              </label>
              {evalLocked && (
                <div className="sd-hint">
                  This claim is {WALLET_STATUS[Number(walletStatus)]} — its
                  evaluation is locked.
                </div>
              )}
              {!evalReadOnly && (
                <div className="sd-hint">
                  The comment is kept with the evaluation — press Save at the
                  top of the page to submit it.
                </div>
              )}
            </div>
            <div className="status-dialog-foot">
              <button
                className="sd-btn ghost"
                onClick={() => setCommentTarget(null)}
              >
                {evalReadOnly ? "Close" : "Cancel"}
              </button>
              {!evalReadOnly && (
                <button className="sd-btn primary" onClick={applyComment}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClaimAIReview;
